import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getKnowledgeBaseArticleEditPath,
  getKnowledgeBaseArticlePath,
  getKnowledgeBaseCollectionPath,
  knowledgeBaseNewArticlePath,
  navItems,
  settingsSectionPaths,
  viewPaths,
} from "./app/data";
import type {
  Activity,
  AccessLog,
  ArchiveItemDetail,
  ArchiveItemSummary,
  AuditEvent,
  ApiKeySummary,
  AppBranding,
  CalendarAssignment,
  CalendarAssignmentInput,
  CalendarAssignmentTypeDefinition,
  ColorMode,
  Customer,
  CustomerCreateInput,
  Expense,
  ExpenseCreateInput,
  ExpenseInvoice,
  ExpenseVendor,
  ExpenseVendorCreateInput,
  GlobalSearchFilter,
  GlobalSearchResult,
  KnowledgeArticle,
  KnowledgeArticleCreateInput,
  KnowledgeCollection,
  KnowledgeCollectionCreateInput,
  KnowledgeBaseSnapshot,
  ProductPlan,
  RoleCapability,
  RolePreset,
  SettingsSection,
  TicketAttachment,
  TicketAttachmentPublicShare,
  TicketCategory,
  TicketCreateInput,
  TicketPriority,
  TicketScope,
  TicketStatus,
  Ticket,
  TicketUpdateInput,
  TenantNotification,
  UserProfile,
  UserTeam,
  View,
  WorkspaceSettings,
  WorkspaceMember,
} from "./app/types";
import {
  appendCustomerActionHistory,
  createRecordId,
  formatDateLabel,
  getCustomerIdFromPath,
  getKnowledgeArticleIdFromPath,
  getKnowledgeCollectionIdFromPath,
  getWorkspaceMemberEffectiveCategoryIds,
  parseTimestampValue,
  getTicketIdFromPath,
  getViewFromPath,
  getSettingsSectionFromPath,
  isKnowledgeArticleEditPath,
  isKnowledgeBaseNewArticlePath,
  normalizeTicketCategoryCustomFields,
  normalizeTicketCustomFieldValues,
  syncCustomerFields,
} from "./app/utils";
import { AnimatedModal, AppShell } from "./app/ui";
import { getInitialAppLanguage, writeStoredAppLanguage, type AppLanguage } from "./app/language";
import { useDomTranslations } from "./app/viewTranslations";
import { CustomerDetailView } from "./views/CustomerDetailView";
import { CustomersView } from "./views/CustomersView";
import { ExpenseTrackerView } from "./views/ExpenseTrackerView";
import { VendorsView } from "./views/VendorsView";
import { DashboardView } from "./views/DashboardView";
import { CalendarView } from "./views/CalendarView.tsx";
import { KnowledgeArticleView } from "./views/KnowledgeArticleView";
import { KnowledgeCollectionView } from "./views/KnowledgeCollectionView";
import { KnowledgeBaseView } from "./views/KnowledgeBaseView";
import { LoginView } from "./views/LoginView";
import { LogsView } from "./views/LogsView";
import { ArchiveView } from "./views/ArchiveView";
import { NewArticleView } from "./views/NewArticleView";
import { NotificationsView } from "./views/NotificationsView";
import { ItemsView } from "./views/ItemsView";
import { SimplifiedReportingView } from "./views/SimplifiedReportingView";
import { SettingsView } from "./views/SettingsView";
import { TenantNotFoundView } from "./views/TenantNotFoundView";
import { TicketDetailView } from "./views/TicketDetailView";
import { TicketsView } from "./views/TicketsView";
import { UsersView } from "./views/UsersView";
import {
  defaultWorkspaceSettings,
} from "./worker/seedData";
import {
  canCreateCalendarAssignments,
  canCreateKnowledgeArticles,
  canDeleteCustomers,
  canDeleteExpenses,
  canDeleteTickets,
  canAccessApi,
  canEditArchive,
  canManageCalendarAssignmentTypes,
  canEditUsers,
  canEditTickets,
  canEditCustomers,
  canEditExpenses,
  canEditAllExpenses,
  canManageAdminSettings,
  canManageKnowledgeBase,
  canManageBilling,
  canManageItems,
  canViewCustomers,
  canViewArchive,
  canViewCalendar,
  canViewExpenses,
  canViewAllExpenses,
  canViewKnowledgeBase,
  canViewNotifications,
  canViewItems,
  canViewReporting,
  canViewSettings,
  canViewTickets,
  canViewUsers,
  canViewVendors,
  canEditVendors,
  isOwnerRole,
  isAgentRole,
} from "./roles";
import "quill/dist/quill.snow.css";
import "./index.css";

const appBrandingStorageKey = "kuii.app-branding";
const profileThemeStorageKey = "kuii.profile-theme";
const profileTimezoneStorageKey = "kuii.profile-timezone";
const profileNavMenuModeStorageKey = "kuii.profile-nav-menu-mode";
const bootstrapCacheStorageKey = "kuii.bootstrap-cache.v1";
const bootstrapCacheTtlMs = 5 * 60 * 1000;
const notificationBannerAnimationDurationMs = 220;
const defaultAppBranding: AppBranding = {
  primaryColor: "#006949",
  consoleName: null,
  logoDataUrl: null,
  logoName: null,
};
const emptyUserProfile: UserProfile = {
  avatarUrl: null,
  avatarUpdatedAt: null,
};
const emptyKnowledgeBase: KnowledgeBaseSnapshot = {
  collections: [],
  featuredArticles: [],
  popularArticles: [],
  articles: [],
};
const emptyCustomer: Customer = {
  id: "",
  name: "",
  initials: "",
  company: "",
  email: "",
  phone: "",
  region: "",
  health: "Healthy",
  accountStatus: "Active",
  renewalDate: "",
  contacts: [],
  products: [],
  notes: [],
  actionHistory: [],
  secondaryContact: {
    name: "",
    email: "",
  },
};
const noCustomerPlaceholder: Customer = {
  ...emptyCustomer,
  name: "No customer",
  initials: "—",
  company: "None",
};
const emptyTicket: Ticket = {
  id: "",
  subject: "",
  description: "",
  category: "General",
  product: "",
  sla: null,
  customerId: "",
  plan: "",
  priority: "Medium",
  status: "Open",
  createdAt: "",
  lastUpdate: "",
  scheduleStartAt: null,
  scheduleAt: null,
  slaPausedAt: null,
  slaPausedTotalMs: 0,
  assignee: null,
  notes: "",
  linkedArticleIds: [],
  customFieldValues: {},
  attachments: [],
};

function normalizeTicketCategoryRecord(category: TicketCategory): TicketCategory {
  return {
    ...category,
    customFields: normalizeTicketCategoryCustomFields(category.customFields),
  };
}

function normalizeTicketLinks(ticket: Ticket): Ticket {
  return {
    ...ticket,
    sla: typeof ticket.sla === "string" && ticket.sla.trim() ? ticket.sla.trim() : null,
    slaPausedAt: typeof ticket.slaPausedAt === "string" && ticket.slaPausedAt.trim() ? ticket.slaPausedAt : null,
    slaPausedTotalMs: Number.isFinite(ticket.slaPausedTotalMs) && ticket.slaPausedTotalMs > 0 ? Math.round(ticket.slaPausedTotalMs) : 0,
    linkedArticleIds: Array.isArray(ticket.linkedArticleIds) ? ticket.linkedArticleIds.filter(Boolean) : [],
    customFieldValues: normalizeTicketCustomFieldValues(ticket.customFieldValues),
    attachments: Array.isArray(ticket.attachments)
      ? ticket.attachments
        .filter(attachment => attachment && typeof attachment.key === "string")
        .map(attachment => ({
          key: attachment.key,
          name: attachment.name,
          contentType: typeof attachment.contentType === "string" && attachment.contentType.trim() ? attachment.contentType : null,
          size: Number.isFinite(attachment.size) ? Number(attachment.size) : 0,
          uploadedAt: attachment.uploadedAt,
          downloadUrl: attachment.downloadUrl,
          publicDownloadUrl: typeof attachment.publicDownloadUrl === "string" && attachment.publicDownloadUrl.trim() ? attachment.publicDownloadUrl : null,
          publicShareOverride: attachment.publicShareOverride === "allow" || attachment.publicShareOverride === "deny" ? attachment.publicShareOverride : null,
          publicDownloadEnabled: attachment.publicDownloadEnabled === true,
        }))
      : [],
  };
}

type TicketSyncGuard = {
  ticket: Ticket;
  freshnessTimestamp: number;
};

function getTicketFreshnessTimestamp(ticket: Ticket) {
  const lastUpdateTimestamp = parseTimestampValue(ticket.lastUpdate);

  if (Number.isFinite(lastUpdateTimestamp)) {
    return lastUpdateTimestamp;
  }

  const createdAtTimestamp = parseTimestampValue(ticket.createdAt);
  return Number.isFinite(createdAtTimestamp) ? createdAtTimestamp : 0;
}

function mergeTicketAttachments(incomingTicket: Ticket, existingTicket?: Ticket | null) {
  if (!existingTicket || incomingTicket.attachments.length > 0 || existingTicket.attachments.length === 0) {
    return incomingTicket;
  }

  return {
    ...incomingTicket,
    attachments: existingTicket.attachments,
  };
}

function mergeGuardedTicket(guardedTicket: Ticket, nextTicket: Ticket) {
  if (guardedTicket.attachments.length > 0 || nextTicket.attachments.length === 0) {
    return guardedTicket;
  }

  return {
    ...guardedTicket,
    attachments: nextTicket.attachments,
  };
}

function normalizeHexColor(value: string, fallback = defaultAppBranding.primaryColor) {
  const normalized = value.trim().replace(/^#?/, "#");
  const hex = normalized.slice(1);

  if (/^[\da-f]{3}$/i.test(hex) || /^[\da-f]{6}$/i.test(hex)) {
    return `#${hex.toLowerCase()}`;
  }

  return fallback;
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const safeHex = normalized.length === 3 ? normalized.split("").map(char => `${char}${char}`).join("") : normalized;
  const value = Number.parseInt(safeHex, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mixColor(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  const target = amount >= 0 ? 255 : 0;
  const intensity = Math.abs(amount);
  const mixChannel = (channel: number) => Math.round(channel + (target - channel) * intensity);

  return `rgb(${mixChannel(r)} ${mixChannel(g)} ${mixChannel(b)})`;
}

function getInitialAppBranding(): AppBranding {
  if (typeof window === "undefined") {
    return defaultAppBranding;
  }

  try {
    const storedValue = window.localStorage.getItem(appBrandingStorageKey);

    if (!storedValue) {
      return defaultAppBranding;
    }

    const parsedValue = JSON.parse(storedValue) as Partial<AppBranding>;

    return {
      primaryColor: normalizeHexColor(parsedValue.primaryColor ?? defaultAppBranding.primaryColor),
      consoleName: typeof parsedValue.consoleName === "string" ? parsedValue.consoleName : null,
      logoDataUrl: typeof parsedValue.logoDataUrl === "string" ? parsedValue.logoDataUrl : null,
      logoName: typeof parsedValue.logoName === "string" ? parsedValue.logoName : null,
    };
  } catch {
    return defaultAppBranding;
  }
}

function getInitialColorMode(): ColorMode {
  if (typeof window === "undefined") {
    return "light";
  }

  const storedValue = window.localStorage.getItem(profileThemeStorageKey);

  if (storedValue === "light" || storedValue === "dark") {
    return storedValue;
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getInitialProfileTimezone() {
  if (typeof window === "undefined") {
    return defaultWorkspaceSettings.timezone;
  }

  return window.localStorage.getItem(profileTimezoneStorageKey)?.trim() || defaultWorkspaceSettings.timezone;
}

function hasStoredProfileTimezonePreference() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(window.localStorage.getItem(profileTimezoneStorageKey)?.trim());
}

function getInitialProfileNavMenuMode() {
  if (typeof window === "undefined") {
    return "labels" as const;
  }

  return window.localStorage.getItem(profileNavMenuModeStorageKey) === "icons" ? "icons" as const : "labels" as const;
}

function getTicketPlanLabel(customer: Customer | null | undefined) {
  if (!customer) {
    return "Support";
  }

  return customer.products.find(product => /support/i.test(product.name))?.name ?? customer.products[0]?.name ?? "Support";
}

function hasConfiguredTicketProduct(productName: string | null | undefined) {
  const normalizedProductName = productName?.trim().toLowerCase() ?? "";

  return Boolean(normalizedProductName) && normalizedProductName !== "none";
}

function getDefaultTicketProduct(customer: Customer | null | undefined) {
  if (!customer) {
    return "";
  }

  return customer.products[0]?.name ?? "Support";
}

function getProductPlanForTicket(customer: Customer | null | undefined, productName: string, productPlans: ProductPlan[]) {
  if (!customer) {
    return null;
  }

  const normalizedProductName = productName.trim().toLowerCase();
  const matchedProduct = customer.products.find(product => product.name.trim().toLowerCase() === normalizedProductName) ?? null;

  if (matchedProduct?.productPlanId) {
    return productPlans.find(plan => plan.id === matchedProduct.productPlanId) ?? null;
  }

  return productPlans.find(plan => plan.name.trim().toLowerCase() === normalizedProductName) ?? null;
}

function getTicketSlaLabel(customer: Customer | null | undefined, productName: string, productPlans: ProductPlan[]) {
  return getProductPlanForTicket(customer, productName, productPlans)?.sla ?? null;
}

type BootstrapSection =
  | "shell"
  | "workspaceUsers"
  | "productPlans"
  | "ticketCategories"
  | "customers"
  | "tickets"
  | "activities"
  | "knowledgeBase"
  | "notifications"
  | "calendar"
  | "expenses"
  | "billingUsage";

type BillingUsage = {
  ticketCount: number;
  articleCount: number;
  storageBytes: number;
  storageObjectCount: number;
};

type BootstrapPayload = Partial<{
  hasArchiveData: boolean;
  branding: AppBranding;
  currentUserProfile: UserProfile;
  currentUserCapabilities: RoleCapability[];
  currentUserEffectiveCategoryIds: string[] | null;
  workspaceSettings: WorkspaceSettings;
  productPlans: ProductPlan[];
  ticketCategories: TicketCategory[];
  customers: Customer[];
  tickets: Ticket[];
  activities: Activity[];
  currentUser: string;
  teamMembers: WorkspaceMember[];
  teams: UserTeam[];
  accessLogs: AccessLog[];
  knowledgeBase: KnowledgeBaseSnapshot;
  notifications: TenantNotification[];
  calendarAssignmentTypes: string[];
  calendarAssignments: CalendarAssignment[];
  billingUsage: BillingUsage;
  expenseVendors: ExpenseVendor[];
  expenses: Expense[];
}> & {
  includedSections: BootstrapSection[];
};

function getBootstrapSectionsForView(
  view: View,
  options?: {
    includeTicketDetail?: boolean;
    includeCustomerDetail?: boolean;
  },
) {
  switch (view) {
    case "Dashboard":
      return ["shell", "tickets", "calendar"] satisfies BootstrapSection[];
    case "Calendar":
      return ["shell", "workspaceUsers", "calendar"] satisfies BootstrapSection[];
    case "Tickets": {
      const sections = new Set<BootstrapSection>(["shell", "workspaceUsers", "ticketCategories", "customers", "tickets"]);

      if (!options?.includeTicketDetail) {
        sections.add("productPlans");
      }

      if (options?.includeTicketDetail) {
        sections.add("activities");
        sections.add("knowledgeBase");
      }

      return [...sections];
    }
    case "Customers": {
      const sections = new Set<BootstrapSection>(["shell", "customers", "tickets"]);

      if (options?.includeCustomerDetail) {
        sections.add("productPlans");
        sections.add("workspaceUsers");
      }

      return [...sections];
    }
    case "Items":
      return ["shell", "productPlans"] satisfies BootstrapSection[];
    case "Vendors":
      return ["shell", "workspaceUsers", "expenses"] satisfies BootstrapSection[];
    case "Expenses":
      return ["shell", "workspaceUsers", "expenses"] satisfies BootstrapSection[];
    case "Logs":
      return ["shell"] satisfies BootstrapSection[];
    case "Archive":
      return ["shell"] satisfies BootstrapSection[];
    case "Users":
      return ["shell", "workspaceUsers", "ticketCategories"] satisfies BootstrapSection[];
    case "Knowledge Base":
      return ["shell", "workspaceUsers", "ticketCategories", "knowledgeBase"] satisfies BootstrapSection[];
    case "Notifications":
      return ["shell", "notifications"] satisfies BootstrapSection[];
    case "Reporting":
      return ["shell", "productPlans", "customers", "tickets", "expenses"] satisfies BootstrapSection[];
    case "Settings":
      return ["shell", "productPlans", "ticketCategories", "tickets", "knowledgeBase", "billingUsage"] satisfies BootstrapSection[];
  }
}

function buildBootstrapRequestPath(sections: readonly BootstrapSection[]) {
  const query = new URLSearchParams();
  query.set("sections", sections.join(","));
  return `/api/bootstrap?${query.toString()}`;
}

type BootstrapCacheEntry = {
  cachedAt: number;
  payload: BootstrapPayload;
};

function getBootstrapCacheKey(scope: string, bootstrapKey: string) {
  return `${bootstrapCacheStorageKey}:${encodeURIComponent(scope)}:${encodeURIComponent(bootstrapKey)}`;
}

function getBootstrapCacheScope(tenant: AuthSessionPayload["tenant"], currentUserEmail: string | null) {
  if (typeof window === "undefined") {
    return "server";
  }

  const tenantScope = tenant?.slug ?? tenant?.hostname ?? window.location.hostname;
  const userScope = currentUserEmail?.trim().toLowerCase() || "anonymous";
  return `${tenantScope}|${userScope}`;
}

function readBootstrapCache(scope: string, bootstrapKey: string): BootstrapPayload | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = getBootstrapCacheKey(scope, bootstrapKey);

  try {
    const rawValue = window.sessionStorage.getItem(storageKey);

    if (!rawValue) {
      return null;
    }

    const entry = JSON.parse(rawValue) as Partial<BootstrapCacheEntry>;

    if (!entry.payload || typeof entry.cachedAt !== "number" || Date.now() - entry.cachedAt > bootstrapCacheTtlMs) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }

    return entry.payload;
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }
}

function writeBootstrapCache(scope: string, bootstrapKey: string, payload: BootstrapPayload) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const entry: BootstrapCacheEntry = {
      cachedAt: Date.now(),
      payload,
    };
    window.sessionStorage.setItem(getBootstrapCacheKey(scope, bootstrapKey), JSON.stringify(entry));
  } catch {
    // Ignore quota and private browsing storage failures.
  }
}

function updateCachedNotificationDismissal(scope: string, notificationId: string, dismissedAt: string | null) {
  if (typeof window === "undefined") {
    return;
  }

  const prefix = `${bootstrapCacheStorageKey}:${encodeURIComponent(scope)}:`;

  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);

      if (!key?.startsWith(prefix)) {
        continue;
      }

      const rawValue = window.sessionStorage.getItem(key);

      if (!rawValue) {
        continue;
      }

      const entry = JSON.parse(rawValue) as Partial<BootstrapCacheEntry>;

      if (!entry.payload || !Array.isArray(entry.payload.notifications)) {
        continue;
      }

      const nextEntry: BootstrapCacheEntry = {
        cachedAt: typeof entry.cachedAt === "number" ? entry.cachedAt : Date.now(),
        payload: {
          ...entry.payload,
          notifications: entry.payload.notifications.map(notification => (
            notification.id === notificationId ? { ...notification, dismissedAt } : notification
          )),
        },
      };

      window.sessionStorage.setItem(key, JSON.stringify(nextEntry));
    }
  } catch {
    // Ignore cache update failures.
  }
}

type AuthSessionPayload = {
  enabled: boolean;
  authenticated: boolean;
  email: string | null;
  name: string | null;
  localPasswordResetEnabled: boolean;
  mustChangePassword: boolean;
  tenant: {
    slug: string;
    role: string | null;
    hostname: string | null;
  } | null;
};

type RestoreTenantDatabaseResponse = {
  ok: boolean;
  tenant?: {
    slug?: string;
    billingPlan?: string;
  };
  restore?: {
    message?: string;
    restoreDate?: string;
    restoreTimestamp?: string;
    retentionDays?: number | null;
  };
};

type TenantDatabaseRestorePointsResponse = {
  ok: boolean;
  tenant?: {
    slug?: string;
    billingPlan?: string;
  };
  restorePoints?: Array<{
    date?: string;
    is_today?: boolean;
    restore_timestamp?: string;
  }>;
  retentionDays?: number | null;
  earliestRestoreTimestamp?: string | null;
  latestRestoreTimestamp?: string | null;
  earliestRestoreDate?: string | null;
  latestRestoreDate?: string | null;
};

type TenantAuthRoute = "login" | "reset-password";

function normalizeTenantAuthPathname(pathname: string) {
  if (!pathname) {
    return "/";
  }

  if (pathname.length > 1) {
    return pathname.replace(/\/+$/, "") || "/";
  }

  return pathname;
}

function getTenantAuthRoute(pathname: string): TenantAuthRoute {
  return normalizeTenantAuthPathname(pathname) === "/reset-password" ? "reset-password" : "login";
}

function getTenantPasswordResetState() {
  if (typeof window === "undefined") {
    return { token: "", error: "" };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    token: params.get("token")?.trim() ?? "",
    error: params.get("error")?.trim() ?? "",
  };
}

function getTenantPasswordResetErrorMessage(errorCode: string) {
  switch (errorCode) {
    case "INVALID_TOKEN":
      return "This password reset link is invalid or has expired. Request a new one below.";
    default:
      return errorCode ? "We could not verify that password reset link. Request a fresh one below." : "";
  }
}

type EmailRoutingSyncState = "idle" | "syncing" | "success" | "error";

const defaultCalendarAssignmentTypes = ["On-call", "Task rotation", "Coverage"] as const satisfies CalendarAssignment["type"][];

function getFallbackKnowledgeBase(): KnowledgeBaseSnapshot {
  return emptyKnowledgeBase;
}

function getStartOfWeek(date = new Date()) {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);
  const dayOfWeek = nextDate.getDay();
  const dayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  nextDate.setDate(nextDate.getDate() + dayOffset);
  return nextDate;
}

function createCalendarAssignmentTimestamp(baseDate: Date, dayOffset: number, hour: number, minute: number) {
  const nextDate = new Date(baseDate);
  nextDate.setDate(baseDate.getDate() + dayOffset);
  nextDate.setHours(hour, minute, 0, 0);
  return nextDate.toISOString();
}

function normalizeCalendarAssignmentTypeName(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function mergeCalendarAssignmentTypes(types: Iterable<unknown>) {
  const defaults = [...defaultCalendarAssignmentTypes];
  const seen = new Set<string>();
  const merged: string[] = [];

  for (const type of defaults) {
    const normalizedType = normalizeCalendarAssignmentTypeName(type);

    if (!normalizedType || seen.has(normalizedType.toLowerCase())) {
      continue;
    }

    seen.add(normalizedType.toLowerCase());
    merged.push(normalizedType);
  }

  for (const type of types) {
    const normalizedType = normalizeCalendarAssignmentTypeName(type);

    if (!normalizedType || seen.has(normalizedType.toLowerCase())) {
      continue;
    }

    seen.add(normalizedType.toLowerCase());
    merged.push(normalizedType);
  }

  return merged;
}

function mergeCalendarAssignmentTypeOptions(
  definitions: Iterable<CalendarAssignmentTypeDefinition>,
  assignments: Iterable<CalendarAssignment>,
) {
  return mergeCalendarAssignmentTypes([
    ...Array.from(definitions, definition => definition.name),
    ...Array.from(assignments, assignment => assignment.type),
  ]);
}

function sortCalendarAssignments(assignments: CalendarAssignment[]) {
  return [...assignments].sort((left, right) => {
    const startDifference = new Date(left.startAt).getTime() - new Date(right.startAt).getTime();

    if (startDifference !== 0) {
      return startDifference;
    }

    return left.title.localeCompare(right.title, undefined, { sensitivity: "base" });
  });
}

function normalizeCalendarAssignmentTypes(value: unknown) {
  if (!Array.isArray(value)) {
    return [...defaultCalendarAssignmentTypes];
  }

  return mergeCalendarAssignmentTypes(value);
}

function normalizeCalendarAssignments(value: unknown): CalendarAssignment[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return sortCalendarAssignments(value.flatMap(item => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const record = item as Partial<CalendarAssignment>;
    const type = normalizeCalendarAssignmentTypeName(record.type);

    if (
      typeof record.id !== "string"
      || typeof record.teamId !== "string"
      || typeof record.title !== "string"
      || !type
      || typeof record.memberEmail !== "string"
      || typeof record.startAt !== "string"
      || typeof record.endAt !== "string"
    ) {
      return [];
    }

    return [{
      id: record.id,
      teamId: record.teamId,
      title: record.title,
      type,
      memberEmail: record.memberEmail,
      startAt: record.startAt,
      endAt: record.endAt,
      notes: typeof record.notes === "string" ? record.notes : "",
    }];
  }));
}

function getInitialCalendarAssignmentTypes() {
  return [...defaultCalendarAssignmentTypes];
}

function getInitialCalendarAssignments() {
  return [];
}

async function parseJsonResponse<T>(response: Response) {
  const text = await readResponseText(response);

  let payload: T | { error?: string } | null = null;

  if (text) {
    try {
      payload = JSON.parse(text) as T | { error?: string };
    } catch {
      throw new Error(`Received an invalid response while saving changes (${response.status}).`);
    }
  }

  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
      ? payload.error
      : `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload as T;
}

async function readResponseText(response: Response) {
  let readableResponse = response;

  try {
    readableResponse = response.clone();
  } catch {
    readableResponse = response;
  }

  try {
    return await readableResponse.text();
  } catch {
    return "";
  }
}

async function getErrorMessage(response: Response) {
  const text = await readResponseText(response);

  if (!text) {
    return `Request failed with status ${response.status}`;
  }

  try {
    const payload = JSON.parse(text) as { error?: string } | null;
    return payload?.error ?? `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit) {
  const response = await fetch(input, init);

  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }

  return (await response.json()) as T;
}

function uploadFormDataWithProgress<T>(url: string, formData: FormData, onProgress?: (percent: number) => void) {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        }
      };
    }

    xhr.onload = () => {
      const text = xhr.responseText;

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(text ? (JSON.parse(text) as T) : ({} as T));
        } catch {
          reject(new Error(`Request failed with status ${xhr.status}`));
        }
        return;
      }

      let message = `Request failed with status ${xhr.status}`;

      if (text) {
        try {
          const payload = JSON.parse(text) as { error?: string } | null;
          message = payload?.error ?? message;
        } catch {
          // Keep the default status-based message when the body is not JSON.
        }
      }

      reject(new Error(message));
    };

    xhr.onerror = () => reject(new Error("Network error while uploading attachments."));
    xhr.onabort = () => reject(new Error("Attachment upload was cancelled."));
    xhr.send(formData);
  });
}

function getFallbackUserTeams(): UserTeam[] {
  return [];
}

function getFallbackWorkspaceMembers(): WorkspaceMember[] {
  return [];
}

function sortWorkspaceMembers(members: WorkspaceMember[]) {
  return [...members].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" }) || left.email.localeCompare(right.email, undefined, { sensitivity: "base" }));
}

function applyTeamsToWorkspaceMembers(members: WorkspaceMember[], teams: UserTeam[]) {
  return sortWorkspaceMembers(members.map(member => {
    const teamIds = member.teamIds.filter(teamId => teams.some(team => team.id === teamId));

    return {
      ...member,
      teamIds,
      effectiveCategoryIds: getWorkspaceMemberEffectiveCategoryIds({
        role: member.role,
        allowedCategoryIds: member.allowedCategoryIds,
        teamIds,
      }, teams),
    };
  }));
}

const reservedRootSubdomains = new Set(["www", "app", "auth", "provisioner", "email"]);

function getRootDomainFromHostname(hostname: string) {
  const normalizedHostname = hostname.trim().toLowerCase();
  const segments = normalizedHostname.split(".").filter(Boolean);

  if (segments.length < 2) {
    return normalizedHostname;
  }

  return segments.slice(-2).join(".");
}

function getTenantHostnameCandidate(hostname: string) {
  const normalizedHostname = hostname.trim().toLowerCase();

  if (!normalizedHostname || normalizedHostname === "localhost" || /^[\d.:]+$/.test(normalizedHostname)) {
    return null;
  }

  const segments = normalizedHostname.split(".").filter(Boolean);

  if (segments.length < 3) {
    return null;
  }

  if (reservedRootSubdomains.has(segments[0] ?? "")) {
    return null;
  }

  return normalizedHostname;
}

export function MainApp() {
  const initialPasswordResetState = getTenantPasswordResetState();
  const [authChecked, setAuthChecked] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [ticketUpdateError, setTicketUpdateError] = useState<string | null>(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [tenantAuthRoute, setTenantAuthRoute] = useState<TenantAuthRoute>(() => {
    if (typeof window === "undefined") {
      return "login";
    }

    return getTenantAuthRoute(window.location.pathname);
  });
  const [localPasswordResetEnabled, setLocalPasswordResetEnabled] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [forcedCurrentPassword, setForcedCurrentPassword] = useState("");
  const [forcedNewPassword, setForcedNewPassword] = useState("");
  const [forcedConfirmPassword, setForcedConfirmPassword] = useState("");
  const [forcedPasswordError, setForcedPasswordError] = useState<string | null>(null);
  const [isUpdatingForcedPassword, setIsUpdatingForcedPassword] = useState(false);
  const [resetToken, setResetToken] = useState(initialPasswordResetState.token);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetRequestError, setResetRequestError] = useState<string | null>(null);
  const [resetRequestNotice, setResetRequestNotice] = useState<string | null>(null);
  const [resetSubmitError, setResetSubmitError] = useState<string | null>(
    initialPasswordResetState.error ? getTenantPasswordResetErrorMessage(initialPasswordResetState.error) : null,
  );
  const [resetSubmitNotice, setResetSubmitNotice] = useState<string | null>(null);
  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
  const [isRequestingPasswordReset, setIsRequestingPasswordReset] = useState(false);
  const [isSubmittingPasswordReset, setIsSubmittingPasswordReset] = useState(false);
  const [tenantNotFoundHost, setTenantNotFoundHost] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<View>(() => {
    if (typeof window === "undefined") {
      return "Dashboard";
    }

    return getViewFromPath(window.location.pathname);
  });
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getSettingsSectionFromPath(window.location.pathname);
  });
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchFilter, setGlobalSearchFilter] = useState<GlobalSearchFilter>("all");
  const [globalSearchCategory, setGlobalSearchCategory] = useState("all");
  const [globalSearchResults, setGlobalSearchResults] = useState<GlobalSearchResult[]>([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TicketScope>("All Tickets");
  const [customerItems, setCustomerItems] = useState<Customer[]>([]);
  const [expenseVendorItems, setExpenseVendorItems] = useState<ExpenseVendor[]>([]);
  const [expenseItems, setExpenseItems] = useState<Expense[]>([]);
  const [productPlanItems, setProductPlanItems] = useState<ProductPlan[]>([]);
  const [ticketCategoryItems, setTicketCategoryItems] = useState<TicketCategory[]>([]);
  const [ticketItems, setTicketItems] = useState<Ticket[]>([]);
  const [activityItems, setActivityItems] = useState<Activity[]>([]);
  const [billingUsage, setBillingUsage] = useState<BillingUsage | null>(null);
  const [hasArchiveData, setHasArchiveData] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBaseSnapshot>(() => getFallbackKnowledgeBase());
  const [tenantNotifications, setTenantNotifications] = useState<TenantNotification[]>([]);
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [currentTenant, setCurrentTenant] = useState<AuthSessionPayload["tenant"]>(null);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>(() => getFallbackWorkspaceMembers());
  const [workspaceTeams, setWorkspaceTeams] = useState<UserTeam[]>(() => getFallbackUserTeams());
  const [currentUserCapabilities, setCurrentUserCapabilities] = useState<RoleCapability[] | null>(null);
  const [currentUserEffectiveCategoryIds, setCurrentUserEffectiveCategoryIds] = useState<string[] | null>(null);
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettings>(defaultWorkspaceSettings);
  const [calendarAssignmentTypeOptions, setCalendarAssignmentTypeOptions] = useState<string[]>(getInitialCalendarAssignmentTypes);
  const [calendarAssignments, setCalendarAssignments] = useState<CalendarAssignment[]>(getInitialCalendarAssignments);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile>(emptyUserProfile);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [emailRoutingSyncState, setEmailRoutingSyncState] = useState<EmailRoutingSyncState>("idle");
  const [emailRoutingSyncMessage, setEmailRoutingSyncMessage] = useState("Mailbox routing will sync when you save.");
  const [profileApiKeys, setProfileApiKeys] = useState<ApiKeySummary[]>([]);
  const [latestGeneratedApiKey, setLatestGeneratedApiKey] = useState<string | null>(null);
  const [hasLoadedProfileApiKeys, setHasLoadedProfileApiKeys] = useState(false);
  const [hasLoadedBootstrap, setHasLoadedBootstrap] = useState(false);
  const [isBootstrapLoading, setIsBootstrapLoading] = useState(false);
  const [lastCompletedBootstrapKey, setLastCompletedBootstrapKey] = useState("");
  const [appBranding, setAppBranding] = useState<AppBranding>(getInitialAppBranding);
  const [appLanguage, setAppLanguage] = useState<AppLanguage>(getInitialAppLanguage);
  const [colorMode, setColorMode] = useState<ColorMode>(getInitialColorMode);
  const [profileTimezone, setProfileTimezone] = useState<string>(getInitialProfileTimezone);
  const [profileNavMenuMode, setProfileNavMenuMode] = useState<"labels" | "icons">(getInitialProfileNavMenuMode);
  const [hasTimezonePreference, setHasTimezonePreference] = useState(hasStoredProfileTimezonePreference);
  const [selectedTicketId, setSelectedTicketId] = useState<string>("");
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [selectedTicketDetailId, setSelectedTicketDetailId] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getTicketIdFromPath(window.location.pathname);
  });
  const [selectedCustomerProfileId, setSelectedCustomerProfileId] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getCustomerIdFromPath(window.location.pathname);
  });
  const [isCreatingArticle, setIsCreatingArticle] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return isKnowledgeBaseNewArticlePath(window.location.pathname);
  });
  const [selectedKnowledgeCollectionId, setSelectedKnowledgeCollectionId] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getKnowledgeCollectionIdFromPath(window.location.pathname);
  });
  const [selectedKnowledgeArticleId, setSelectedKnowledgeArticleId] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getKnowledgeArticleIdFromPath(window.location.pathname);
  });
  const [isEditingKnowledgeArticle, setIsEditingKnowledgeArticle] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return isKnowledgeArticleEditPath(window.location.pathname);
  });
  const tenantHostnameCandidate = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getTenantHostnameCandidate(window.location.hostname);
  }, []);
  const tenantRootDomain = useMemo(() => {
    if (typeof window === "undefined") {
      return "kuii.net";
    }

    return getRootDomainFromHostname(window.location.hostname) || "kuii.net";
  }, []);
  const lastTrackedKnowledgeArticleIdRef = useRef<string | null>(null);
  const requestedTicketAttachmentIdsRef = useRef<Set<string>>(new Set());
  const pendingTicketCreationIdsRef = useRef<Set<string>>(new Set());
  const deletedTicketIdsRef = useRef<Set<string>>(new Set());
  const ticketSyncGuardsRef = useRef<Map<string, TicketSyncGuard>>(new Map());
  const latestTicketMutationRequestIdsRef = useRef<Map<string, number>>(new Map());
  const nextTicketMutationRequestIdRef = useRef(0);
  const latestBootstrapRequestIdRef = useRef(0);
  const ticketItemsRef = useRef<Ticket[]>(ticketItems);
  const profileApiKeysLoadPromiseRef = useRef<Promise<void> | null>(null);
  const [loadingTicketAttachmentIds, setLoadingTicketAttachmentIds] = useState<string[]>([]);
  const [syncingTicketIds, setSyncingTicketIds] = useState<string[]>([]);
  const [renderedBannerNotification, setRenderedBannerNotification] = useState<TenantNotification | null>(null);
  const [isNotificationBannerVisible, setIsNotificationBannerVisible] = useState(false);
  const renderedBannerNotificationIdRef = useRef<string | null>(null);
  const notificationBannerAnimationTimeoutRef = useRef<number | null>(null);

  useDomTranslations(appLanguage);

  useEffect(() => {
    ticketItemsRef.current = ticketItems;
  }, [ticketItems]);

  const currentBootstrapSections = useMemo(
    () => getBootstrapSectionsForView(activeView, {
      includeTicketDetail: Boolean(selectedTicketDetailId),
      includeCustomerDetail: Boolean(selectedCustomerProfileId),
    }),
    [activeView, selectedCustomerProfileId, selectedTicketDetailId],
  );
  const currentBootstrapKey = useMemo(
    () => currentBootstrapSections.join(","),
    [currentBootstrapSections],
  );
  const currentBootstrapCacheScope = useMemo(
    () => getBootstrapCacheScope(currentTenant, currentUserEmail),
    [currentTenant, currentUserEmail],
  );
  const isCurrentViewBootstrapPending = authChecked && isAuthenticated && !mustChangePassword && currentBootstrapKey !== lastCompletedBootstrapKey;

  const markTicketAsSyncing = useCallback((ticketId: string) => {
    setSyncingTicketIds(currentTicketIds => (
      currentTicketIds.includes(ticketId)
        ? currentTicketIds
        : [...currentTicketIds, ticketId]
    ));
  }, []);

  const clearTicketSyncing = useCallback((ticketId: string) => {
    setSyncingTicketIds(currentTicketIds => currentTicketIds.filter(currentTicketId => currentTicketId !== ticketId));
  }, []);

  const updateTicketSyncGuard = useCallback((ticket: Ticket) => {
    const normalizedTicket = normalizeTicketLinks(ticket);

    ticketSyncGuardsRef.current.set(normalizedTicket.id, {
      ticket: normalizedTicket,
      freshnessTimestamp: getTicketFreshnessTimestamp(normalizedTicket),
    });

    return normalizedTicket;
  }, []);

  const clearTicketSyncGuard = useCallback((ticketId: string) => {
    ticketSyncGuardsRef.current.delete(ticketId);
  }, []);

  const applyBootstrapPayload = useCallback((data: BootstrapPayload) => {
    setHasLoadedBootstrap(true);
    setTenantNotFoundHost(null);

    if ("hasArchiveData" in data) {
      setHasArchiveData(Boolean(data.hasArchiveData));
    }

    if ("branding" in data && data.branding) {
      setAppBranding(data.branding);
    }

    if ("currentUserProfile" in data) {
      setCurrentUserProfile(data.currentUserProfile ?? emptyUserProfile);
    }

    if ("currentUserCapabilities" in data) {
      setCurrentUserCapabilities(data.currentUserCapabilities ?? []);
    }

    if ("currentUserEffectiveCategoryIds" in data) {
      setCurrentUserEffectiveCategoryIds(data.currentUserEffectiveCategoryIds ?? null);
    }

    if ("workspaceSettings" in data) {
      const nextWorkspaceSettings = { ...defaultWorkspaceSettings, ...(data.workspaceSettings ?? {}) };
      setWorkspaceSettings(nextWorkspaceSettings);

      if (!hasTimezonePreference && nextWorkspaceSettings.timezone) {
        setProfileTimezone(nextWorkspaceSettings.timezone);
      }
    }

    if ("customers" in data && Array.isArray(data.customers)) {
      setCustomerItems(data.customers.map(syncCustomerFields));
    }

    if ("expenseVendors" in data && Array.isArray(data.expenseVendors)) {
      setExpenseVendorItems(data.expenseVendors);
    }

    if ("expenses" in data && Array.isArray(data.expenses)) {
      setExpenseItems(data.expenses);
    }

    if ("productPlans" in data && Array.isArray(data.productPlans)) {
      setProductPlanItems(data.productPlans);
    }

    if ("ticketCategories" in data && Array.isArray(data.ticketCategories)) {
      setTicketCategoryItems(data.ticketCategories.map(normalizeTicketCategoryRecord));
    }

    if ("tickets" in data && Array.isArray(data.tickets)) {
      const deletedTicketIds = deletedTicketIdsRef.current;
      const existingTicketsById = new Map(ticketItemsRef.current.map(ticket => [ticket.id, ticket]));
      const nextTickets = data.tickets.flatMap(ticket => {
        const normalizedTicket = normalizeTicketLinks(ticket);

        if (deletedTicketIds.has(normalizedTicket.id)) {
          return [];
        }

        const existingTicket = existingTicketsById.get(normalizedTicket.id);
        const mergedTicket = mergeTicketAttachments(normalizedTicket, existingTicket);
        const guardedTicket = ticketSyncGuardsRef.current.get(mergedTicket.id);

        if (guardedTicket && guardedTicket.freshnessTimestamp > getTicketFreshnessTimestamp(mergedTicket)) {
          return [mergeGuardedTicket(guardedTicket.ticket, mergedTicket)];
        }

        if (guardedTicket && guardedTicket.freshnessTimestamp <= getTicketFreshnessTimestamp(mergedTicket)) {
          ticketSyncGuardsRef.current.delete(mergedTicket.id);
        }

        return [mergedTicket];
      });
      const nextTicketIds = new Set(nextTickets.map(ticket => ticket.id));
      const preservedTickets = ticketItemsRef.current.filter(ticket => {
        if (deletedTicketIds.has(ticket.id)) {
          return false;
        }

        if (nextTicketIds.has(ticket.id)) {
          return false;
        }

        return pendingTicketCreationIdsRef.current.has(ticket.id) || ticketSyncGuardsRef.current.has(ticket.id);
      }).map(ticket => {
        const guardedTicket = ticketSyncGuardsRef.current.get(ticket.id);
        return guardedTicket ? mergeGuardedTicket(guardedTicket.ticket, ticket) : ticket;
      });
      const mergedTickets = [...preservedTickets, ...nextTickets];
      const mergedTicketIds = new Set(mergedTickets.map(ticket => ticket.id));

      requestedTicketAttachmentIdsRef.current = new Set(
        [...requestedTicketAttachmentIdsRef.current].filter(ticketId => mergedTicketIds.has(ticketId)),
      );
      setLoadingTicketAttachmentIds(currentTicketIds => currentTicketIds.filter(ticketId => mergedTicketIds.has(ticketId)));
      setTicketItems(mergedTickets);
      setSelectedTicketId(current => (current && mergedTickets.some(ticket => ticket.id === current) ? current : mergedTickets[0]?.id ?? ""));
    }

    if ("activities" in data && Array.isArray(data.activities)) {
      setActivityItems(data.activities);
    }

    if ("billingUsage" in data && data.billingUsage) {
      setBillingUsage(data.billingUsage);
    }

    if ("knowledgeBase" in data && data.knowledgeBase) {
      setKnowledgeBase(data.knowledgeBase);
    }

    if ("notifications" in data && Array.isArray(data.notifications)) {
      setTenantNotifications(data.notifications);
    }

    if ("currentUser" in data && typeof data.currentUser === "string") {
      setCurrentUserName(data.currentUser);
    }

    if ("teams" in data) {
      setWorkspaceTeams(data.teams ?? getFallbackUserTeams());
    }

    if ("teamMembers" in data) {
      const nextTeamMembers = data.teamMembers ?? getFallbackWorkspaceMembers();
      setWorkspaceMembers(nextTeamMembers);

      if (currentUserEmail) {
        const matchingMember = nextTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail.toLowerCase()) ?? null;

        if (matchingMember?.role) {
          setCurrentUserRole(matchingMember.role);
        }

        if (matchingMember) {
          setCurrentUserEffectiveCategoryIds(matchingMember.effectiveCategoryIds ?? matchingMember.allowedCategoryIds ?? null);
        }
      }
    }

    if ("calendarAssignmentTypes" in data && Array.isArray(data.calendarAssignmentTypes)) {
      setCalendarAssignmentTypeOptions(normalizeCalendarAssignmentTypes(data.calendarAssignmentTypes));
    }

    if ("calendarAssignments" in data && Array.isArray(data.calendarAssignments)) {
      const nextAssignments = normalizeCalendarAssignments(data.calendarAssignments);
      setCalendarAssignments(nextAssignments);
    }
  }, [currentUserEmail, hasTimezonePreference]);

  const getKnowledgeBaseRoutePath = (options?: {
    isCreatingArticle?: boolean;
    collectionId?: string | null;
    articleId?: string | null;
    isEditingKnowledgeArticle?: boolean;
  }) => {
    const nextIsCreatingArticle = options?.isCreatingArticle ?? isCreatingArticle;
    const nextCollectionId = options?.collectionId ?? selectedKnowledgeCollectionId;
    const nextArticleId = options?.articleId ?? selectedKnowledgeArticleId;
    const nextIsEditingKnowledgeArticle = options?.isEditingKnowledgeArticle ?? isEditingKnowledgeArticle;

    if (nextIsCreatingArticle) {
      return knowledgeBaseNewArticlePath;
    }

    if (nextArticleId) {
      return nextIsEditingKnowledgeArticle ? getKnowledgeBaseArticleEditPath(nextArticleId) : getKnowledgeBaseArticlePath(nextArticleId);
    }

    if (nextCollectionId) {
      return getKnowledgeBaseCollectionPath(nextCollectionId);
    }

    return viewPaths["Knowledge Base"];
  };

  const getCurrentRoutePath = (options?: {
    activeView?: View;
    ticketId?: string | null;
    customerId?: string | null;
    isCreatingArticle?: boolean;
    collectionId?: string | null;
    articleId?: string | null;
    isEditingKnowledgeArticle?: boolean;
  }) => {
    const nextActiveView = options?.activeView ?? activeView;
    const nextTicketId = options?.ticketId ?? selectedTicketDetailId;
    const nextCustomerId = options?.customerId ?? selectedCustomerProfileId;

    if (nextActiveView === "Knowledge Base") {
      return getKnowledgeBaseRoutePath({
        isCreatingArticle: options?.isCreatingArticle,
        collectionId: options?.collectionId,
        articleId: options?.articleId,
        isEditingKnowledgeArticle: options?.isEditingKnowledgeArticle,
      });
    }

    if (nextActiveView === "Tickets" && nextTicketId) {
      return `${viewPaths.Tickets}/${encodeURIComponent(nextTicketId)}`;
    }

    if (nextActiveView === "Customers" && nextCustomerId) {
      return `${viewPaths.Customers}/${encodeURIComponent(nextCustomerId)}`;
    }

    if (nextActiveView === "Settings" && settingsSection) {
      return settingsSectionPaths[settingsSection];
    }

    return viewPaths[nextActiveView];
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const controller = new AbortController();

    const loadSession = async () => {
      try {
        const response = await fetch("/api/auth/session", { signal: controller.signal });

        if (response.status === 404) {
          if (tenantHostnameCandidate) {
            setTenantNotFoundHost(tenantHostnameCandidate);
            setAuthEnabled(false);
            setIsAuthenticated(false);
            setAuthChecked(true);
            setLocalPasswordResetEnabled(false);
            setMustChangePassword(false);
            setCurrentTenant(null);
            setCurrentUserProfile(emptyUserProfile);
            return;
          }

          setAuthEnabled(false);
          setIsAuthenticated(true);
          setAuthChecked(true);
          setLocalPasswordResetEnabled(false);
          setMustChangePassword(false);
          setCurrentTenant(null);
          setCurrentUserProfile(emptyUserProfile);
          return;
        }

        const data = await parseJsonResponse<AuthSessionPayload>(response);
        setTenantNotFoundHost(null);
        setAuthEnabled(data.enabled);
        setIsAuthenticated(!data.enabled || data.authenticated);
        setAuthChecked(true);
        setAuthError(null);
        setLocalPasswordResetEnabled(Boolean(data.localPasswordResetEnabled));
        setMustChangePassword(Boolean(data.mustChangePassword));
        setCurrentUserEmail(data.email ?? null);
        setCurrentUserRole(data.tenant?.role ?? null);
        setCurrentTenant(data.tenant ?? null);
        setCurrentUserProfile(emptyUserProfile);

        if (data.email) {
          setLoginEmail(data.email);
        }

        if (data.name) {
          setCurrentUserName(data.name);
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        setAuthEnabled(true);
        setIsAuthenticated(false);
        setAuthChecked(true);
        setAuthError("Unable to verify access right now. Please try again.");
        setLocalPasswordResetEnabled(false);
        setMustChangePassword(false);
        setCurrentTenant(null);
        setCurrentUserProfile(emptyUserProfile);
      }
    };

    void loadSession();

    return () => {
      controller.abort();
    };
  }, [tenantHostnameCandidate]);

  useEffect(() => {
    if (typeof window === "undefined" || !authChecked || !isAuthenticated || mustChangePassword) {
      return;
    }

    const controller = new AbortController();
    const bootstrapRequestId = latestBootstrapRequestIdRef.current + 1;

    latestBootstrapRequestIdRef.current = bootstrapRequestId;

    const loadBootstrap = async () => {
      try {
        const cachedPayload = readBootstrapCache(currentBootstrapCacheScope, currentBootstrapKey);

        if (cachedPayload) {
          applyBootstrapPayload(cachedPayload);
          setLastCompletedBootstrapKey(currentBootstrapKey);
          setIsBootstrapLoading(false);
        } else {
          setIsBootstrapLoading(true);
        }

        const response = await fetch(buildBootstrapRequestPath(currentBootstrapSections), {
          signal: controller.signal,
          cache: "no-store",
        });

        if (response.status === 404 && tenantHostnameCandidate) {
          setTenantNotFoundHost(tenantHostnameCandidate);
          return;
        }

        const data = await parseJsonResponse<BootstrapPayload>(response);

        if (bootstrapRequestId !== latestBootstrapRequestIdRef.current) {
          return;
        }

        writeBootstrapCache(currentBootstrapCacheScope, currentBootstrapKey, data);
        applyBootstrapPayload(data);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.warn("Failed to load bootstrap data from API, using local fallback data.", error);
      } finally {
        if (bootstrapRequestId === latestBootstrapRequestIdRef.current) {
          setIsBootstrapLoading(false);
          setLastCompletedBootstrapKey(currentBootstrapKey);
        }
      }
    };

    void loadBootstrap();

    return () => {
      controller.abort();
    };
  }, [applyBootstrapPayload, authChecked, currentBootstrapCacheScope, currentBootstrapKey, currentBootstrapSections, isAuthenticated, mustChangePassword, tenantHostnameCandidate]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handlePopState = () => {
      setTenantAuthRoute(getTenantAuthRoute(window.location.pathname));
      setActiveView(getViewFromPath(window.location.pathname));
      setSettingsSection(getSettingsSectionFromPath(window.location.pathname));
      setSelectedTicketDetailId(getTicketIdFromPath(window.location.pathname));
      setSelectedCustomerProfileId(getCustomerIdFromPath(window.location.pathname));
      setIsCreatingArticle(isKnowledgeBaseNewArticlePath(window.location.pathname));
      setSelectedKnowledgeCollectionId(getKnowledgeCollectionIdFromPath(window.location.pathname));
      setSelectedKnowledgeArticleId(getKnowledgeArticleIdFromPath(window.location.pathname));
      setIsEditingKnowledgeArticle(isKnowledgeArticleEditPath(window.location.pathname));
      const nextResetState = getTenantPasswordResetState();
      setResetToken(nextResetState.token);
      setResetSubmitError(nextResetState.error ? getTenantPasswordResetErrorMessage(nextResetState.error) : null);
      setResetSubmitNotice(null);
    };

    window.addEventListener("popstate", handlePopState);

    if (authChecked && isAuthenticated && !mustChangePassword) {
      const expectedPath = getCurrentRoutePath();
      if (window.location.pathname !== expectedPath) {
        window.history.replaceState({}, "", expectedPath);
      }
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [authChecked, getCurrentRoutePath, isAuthenticated, mustChangePassword]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    setHasLoadedBootstrap(false);
    setIsBootstrapLoading(false);
    setLastCompletedBootstrapKey("");
    requestedTicketAttachmentIdsRef.current.clear();
    pendingTicketCreationIdsRef.current.clear();
    ticketSyncGuardsRef.current.clear();
    latestTicketMutationRequestIdsRef.current.clear();
    setLoadingTicketAttachmentIds([]);
    setSyncingTicketIds([]);
    setCurrentUserCapabilities(null);
    setCurrentUserEffectiveCategoryIds(null);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authChecked || !isAuthenticated || !selectedTicketDetailId) {
      return;
    }

    const selectedTicketRecord = ticketItems.find(ticket => ticket.id === selectedTicketDetailId);

    if (!selectedTicketRecord) {
      return;
    }

    if (pendingTicketCreationIdsRef.current.has(selectedTicketRecord.id)) {
      return;
    }

    if (selectedTicketRecord.attachments.length > 0 || requestedTicketAttachmentIdsRef.current.has(selectedTicketRecord.id)) {
      return;
    }

    requestedTicketAttachmentIdsRef.current.add(selectedTicketRecord.id);
    setLoadingTicketAttachmentIds(currentTicketIds => (
      currentTicketIds.includes(selectedTicketRecord.id)
        ? currentTicketIds
        : [...currentTicketIds, selectedTicketRecord.id]
    ));

    const controller = new AbortController();

    void (async () => {
      try {
        const attachments = await requestJson<TicketAttachment[]>(`/api/tickets/${encodeURIComponent(selectedTicketRecord.id)}/attachments`, {
          signal: controller.signal,
        });

        setTicketItems(currentTickets => currentTickets.map(ticket => (
          ticket.id === selectedTicketRecord.id
            ? normalizeTicketLinks({ ...ticket, attachments })
            : ticket
        )));
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        requestedTicketAttachmentIdsRef.current.delete(selectedTicketRecord.id);
        console.warn("Failed to load ticket attachments.", error);
      } finally {
        setLoadingTicketAttachmentIds(currentTicketIds => currentTicketIds.filter(ticketId => ticketId !== selectedTicketRecord.id));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [authChecked, isAuthenticated, selectedTicketDetailId, ticketItems]);

  useEffect(() => {
    setCalendarAssignmentTypeOptions(currentTypes => mergeCalendarAssignmentTypes([
      ...currentTypes,
      ...calendarAssignments.map(assignment => assignment.type),
    ]));
  }, [calendarAssignments]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(appBrandingStorageKey, JSON.stringify(appBranding));
  }, [appBranding]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const { r, g, b } = hexToRgb(appBranding.primaryColor);
    const variables = {
      "--brand-primary": appBranding.primaryColor,
      "--brand-primary-strong": mixColor(appBranding.primaryColor, -0.14),
      "--brand-primary-tint": mixColor(appBranding.primaryColor, 0.42),
      "--brand-primary-rgb": `${r}, ${g}, ${b}`,
      "--brand-primary-soft": `rgba(${r}, ${g}, ${b}, 0.1)`,
      "--brand-primary-soft-strong": `rgba(${r}, ${g}, ${b}, 0.16)`,
      "--brand-primary-shadow": `rgba(${r}, ${g}, ${b}, 0.2)`,
      "--brand-primary-shadow-strong": `rgba(${r}, ${g}, ${b}, 0.3)`,
      "--brand-primary-ring": `rgba(${r}, ${g}, ${b}, 0.18)`,
    };

    for (const [name, value] of Object.entries(variables)) {
      window.document.documentElement.style.setProperty(name, value);
      window.document.body.style.setProperty(name, value);
    }
  }, [appBranding]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    writeStoredAppLanguage(appLanguage);
    window.document.documentElement.lang = appLanguage;
  }, [appLanguage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(profileThemeStorageKey, colorMode);
    window.document.documentElement.dataset.theme = colorMode;
    window.document.body.dataset.theme = colorMode;
    window.document.documentElement.style.colorScheme = colorMode;
  }, [colorMode]);

  useEffect(() => {
    if (!globalSearchQuery.trim()) {
      setGlobalSearchFilter("all");
      setGlobalSearchCategory("all");
    }
  }, [globalSearchQuery]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(profileTimezoneStorageKey, profileTimezone);
    setHasTimezonePreference(true);
  }, [profileTimezone]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(profileNavMenuModeStorageKey, profileNavMenuMode);
  }, [profileNavMenuMode]);

  useEffect(() => {
    const productPlanNameById = new Map(productPlanItems.map(plan => [plan.id, plan.name]));

    setCustomerItems(currentCustomers => {
      let hasChanges = false;

      const nextCustomers = currentCustomers.map(customer => {
        let customerChanged = false;

        const nextProducts = customer.products.map(product => {
          if (!product.productPlanId) {
            return product;
          }

          const resolvedName = productPlanNameById.get(product.productPlanId) ?? product.name;

          if (resolvedName === product.name) {
            return product;
          }

          hasChanges = true;
          customerChanged = true;

          return {
            ...product,
            name: resolvedName,
          };
        });

        return customerChanged
          ? {
              ...customer,
              products: nextProducts,
            }
          : customer;
      });

      return hasChanges ? nextCustomers : currentCustomers;
    });
  }, [productPlanItems]);

  const currentWorkspaceMember = useMemo(
    () => workspaceMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase()) ?? null,
    [currentUserEmail, workspaceMembers],
  );
  const currentKnowledgeUserEmail = useMemo(() => {
    if (currentWorkspaceMember?.email) {
      return currentWorkspaceMember.email;
    }

    if (currentUserEmail) {
      return currentUserEmail;
    }

    const matchingMember = workspaceMembers.find(member => member.name.trim().toLowerCase() === currentUserName.trim().toLowerCase()) ?? null;
    return matchingMember?.email ?? null;
  }, [currentUserEmail, currentUserName, currentWorkspaceMember?.email, workspaceMembers]);
  const effectiveCurrentRole = currentWorkspaceMember?.role ?? currentUserRole ?? null;
  const hasCurrentCapability = useCallback((capability: RoleCapability) => currentUserCapabilities?.includes(capability) ?? false, [currentUserCapabilities]);
  const hasLoadedCurrentUserCapabilities = currentUserCapabilities !== null;
  const canManageOwnApiKeys = authEnabled && (hasLoadedCurrentUserCapabilities ? hasCurrentCapability("api:access") : canAccessApi(effectiveCurrentRole));
  const canGenerateApiKeys = canManageOwnApiKeys && workspaceSettings.apiAccessEnabled;
  const canAccessCustomerView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("customer:view") || hasCurrentCapability("customers:edit") || hasCurrentCapability("customers:delete")
    : canViewCustomers(effectiveCurrentRole);
  const canManageCustomerAccounts = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("customers:edit")
    : canEditCustomers(effectiveCurrentRole);
  const canRemoveCustomerAccounts = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("customers:delete")
    : canDeleteCustomers(effectiveCurrentRole);
  const canAccessTicketView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("tickets:view") || hasCurrentCapability("tickets:edit") || hasCurrentCapability("tickets:delete")
    : canViewTickets(effectiveCurrentRole);
  const canManageTickets = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("tickets:edit")
    : canEditTickets(effectiveCurrentRole);
  const canRemoveTickets = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("tickets:delete")
    : canDeleteTickets(effectiveCurrentRole);
  const canCreateKbArticles = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("kb:create")
    : canCreateKnowledgeArticles(effectiveCurrentRole);
  const canManageKbContent = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("kb:manage")
    : canManageKnowledgeBase(effectiveCurrentRole);
  const canAccessKnowledgeBaseView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("kb:view") || hasCurrentCapability("kb:create") || hasCurrentCapability("kb:manage")
    : canViewKnowledgeBase(effectiveCurrentRole);
  const canAccessItemsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("items:view") || hasCurrentCapability("items:manage")
    : canViewItems(effectiveCurrentRole);
  const canAccessUsersView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("users:view") || hasCurrentCapability("users:manage")
    : canViewUsers(effectiveCurrentRole);
  const canEditUsersView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("users:manage")
    : canEditUsers(effectiveCurrentRole);
  const canAccessNotificationsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("notifications:view")
    : canViewNotifications(effectiveCurrentRole);
  const canAccessArchiveView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("archive:view") || hasCurrentCapability("archive:edit")
    : canViewArchive(effectiveCurrentRole);
  const canEditArchiveView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("archive:edit")
    : canEditArchive(effectiveCurrentRole);
  const canAccessSettingsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("settings:view") || hasCurrentCapability("settings:manage")
    : canViewSettings(effectiveCurrentRole);
  const canAccessReportingView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("reporting:view") || hasCurrentCapability("reporting:manage")
    : canViewReporting(effectiveCurrentRole);
  const canManageItemsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("items:manage")
    : canManageItems(effectiveCurrentRole);
  const canManageAdminSettingsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("settings:manage")
    : canManageAdminSettings(effectiveCurrentRole);
  const canManageCalendarAssignmentTypesView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("calendar:manage")
    : canManageCalendarAssignmentTypes(effectiveCurrentRole);
  const canCreateCalendarAssignmentsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("calendar:create") || hasCurrentCapability("calendar:manage")
    : canCreateCalendarAssignments(effectiveCurrentRole);
  const canAccessCalendarView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("calendar:view") || hasCurrentCapability("calendar:create") || hasCurrentCapability("calendar:manage")
    : canViewCalendar(effectiveCurrentRole);
  const canManageBillingView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("billing:manage")
    : canManageBilling(effectiveCurrentRole);
  const canAccessExpenseView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("expenses:view") || hasCurrentCapability("expenses:edit") || hasCurrentCapability("expenses:delete") || hasCurrentCapability("expenses:view-all") || hasCurrentCapability("expenses:edit-all")
    : canViewExpenses(effectiveCurrentRole);
  const canManageExpensesView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("expenses:edit")
    : canEditExpenses(effectiveCurrentRole);
  const canDeleteExpensesView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("expenses:delete")
    : canDeleteExpenses(effectiveCurrentRole);
  const canViewAllExpensesView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("expenses:view-all") || hasCurrentCapability("expenses:edit-all")
    : canViewAllExpenses(effectiveCurrentRole);
  const canEditAllExpensesView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("expenses:edit-all")
    : canEditAllExpenses(effectiveCurrentRole);
  const canAccessVendorView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("vendors:view") || hasCurrentCapability("vendors:edit") || hasCurrentCapability("vendors:delete")
    : canViewVendors(effectiveCurrentRole);
  const canManageVendorsView = hasLoadedCurrentUserCapabilities
    ? hasCurrentCapability("vendors:edit")
    : canEditVendors(effectiveCurrentRole);
  const allowedViewSet = useMemo(() => {
    return new Set(
      navItems.filter(view => {
        if (view === "Tickets") {
          return canAccessTicketView;
        }

        if (view === "Calendar") {
          return canAccessCalendarView;
        }

        if (view === "Knowledge Base") {
          return canAccessKnowledgeBaseView;
        }

        if (view === "Customers") {
          return canAccessCustomerView;
        }

        if (view === "Items") {
          return canAccessItemsView;
        }

        if (view === "Users") {
          return canAccessUsersView;
        }

        if (view === "Logs") {
          return canAccessUsersView;
        }

        if (view === "Archive") {
          return canAccessArchiveView;
        }

        if (view === "Notifications") {
          return canAccessNotificationsView;
        }

        if (view === "Settings") {
          return canAccessSettingsView;
        }

        if (view === "Reporting") {
          return canAccessReportingView;
        }

        if (view === "Vendors") {
          return canAccessVendorView;
        }

        if (view === "Expenses") {
          return canAccessExpenseView;
        }

        return true;
      }),
    );
  }, [canAccessArchiveView, canAccessCalendarView, canAccessCustomerView, canAccessExpenseView, canAccessKnowledgeBaseView, canAccessNotificationsView, canAccessItemsView, canAccessReportingView, canAccessSettingsView, canAccessTicketView, canAccessUsersView, canAccessVendorView, hasArchiveData]);
  const availableViews = useMemo(() => navItems.filter(view => allowedViewSet.has(view)), [allowedViewSet]);
  const isScopedAgent = isAgentRole(effectiveCurrentRole) && currentUserEffectiveCategoryIds !== null;
  const visibleTicketCategories = useMemo(() => {
    if (!isAgentRole(effectiveCurrentRole)) {
      return ticketCategoryItems;
    }

    const allowedCategoryIds = currentUserEffectiveCategoryIds;

    if (!allowedCategoryIds) {
      return ticketCategoryItems;
    }

    const allowedCategoryIdSet = new Set(allowedCategoryIds);
    return ticketCategoryItems.filter(category => allowedCategoryIdSet.has(category.id));
  }, [currentUserEffectiveCategoryIds, effectiveCurrentRole, ticketCategoryItems]);
  const visibleCategoryNameSet = useMemo(() => new Set(visibleTicketCategories.map(category => category.name)), [visibleTicketCategories]);
  const visibleTicketItems = useMemo(() => {
    if (!isScopedAgent) {
      return ticketItems;
    }

    return ticketItems.filter(ticket => visibleCategoryNameSet.has(ticket.category));
  }, [isScopedAgent, ticketItems, visibleCategoryNameSet]);
  const visibleKnowledgeBase = useMemo<KnowledgeBaseSnapshot>(() => {
    if (!isScopedAgent) {
      return knowledgeBase;
    }

    const visibleArticles = knowledgeBase.articles.filter(article => article.visibility === "private" || visibleCategoryNameSet.has(article.audience));
    const visibleArticleIdSet = new Set(visibleArticles.map(article => article.id));
    const visibleWorkspaceArticles = visibleArticles.filter(article => article.visibility === "workspace");

    return {
      collections: knowledgeBase.collections.map(collection => ({
        ...collection,
        articleCount: visibleWorkspaceArticles.filter(article => article.collectionId === collection.id).length,
      })),
      featuredArticles: knowledgeBase.featuredArticles.filter(article => visibleArticleIdSet.has(article.id)),
      popularArticles: knowledgeBase.popularArticles.filter(article => visibleArticleIdSet.has(article.id)),
      articles: visibleArticles,
    };
  }, [isScopedAgent, knowledgeBase, visibleCategoryNameSet]);
  const activeBannerNotification = useMemo(() => {
    return tenantNotifications.find(notification => notification.showInBanner && !notification.dismissedAt) ?? null;
  }, [tenantNotifications]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (notificationBannerAnimationTimeoutRef.current !== null) {
      window.clearTimeout(notificationBannerAnimationTimeoutRef.current);
      notificationBannerAnimationTimeoutRef.current = null;
    }

    if (activeBannerNotification) {
      if (renderedBannerNotificationIdRef.current === activeBannerNotification.id) {
        setRenderedBannerNotification(activeBannerNotification);
        setIsNotificationBannerVisible(true);
        return;
      }

      renderedBannerNotificationIdRef.current = activeBannerNotification.id;
      setRenderedBannerNotification(activeBannerNotification);
      setIsNotificationBannerVisible(false);

      const animationFrame = window.requestAnimationFrame(() => {
        setIsNotificationBannerVisible(true);
      });

      return () => {
        window.cancelAnimationFrame(animationFrame);
      };
    }

    setIsNotificationBannerVisible(false);
    notificationBannerAnimationTimeoutRef.current = window.setTimeout(() => {
      setRenderedBannerNotification(null);
      renderedBannerNotificationIdRef.current = null;
      notificationBannerAnimationTimeoutRef.current = null;
    }, notificationBannerAnimationDurationMs);

    return () => {
      if (notificationBannerAnimationTimeoutRef.current !== null) {
        window.clearTimeout(notificationBannerAnimationTimeoutRef.current);
        notificationBannerAnimationTimeoutRef.current = null;
      }
    };
  }, [activeBannerNotification]);

  const handleNavigate = (view: View) => {
    if (!allowedViewSet.has(view)) {
      return;
    }

    setActiveView(view);
    setSelectedTicketDetailId(null);
    setSelectedCustomerProfileId(null);
    setIsCreatingArticle(false);
    setSelectedKnowledgeCollectionId(null);
    setSelectedKnowledgeArticleId(null);
    setIsEditingKnowledgeArticle(false);
    if (view !== "Settings") {
      setSettingsSection(null);
    }

    if (typeof window === "undefined") {
      return;
    }

    const nextPath = view === "Settings" && settingsSection ? settingsSectionPaths[settingsSection] : viewPaths[view];
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  };

  const handleNavigateToSettingsSection = useCallback((section: SettingsSection) => {
    setSettingsSection(section);
    setActiveView("Settings");

    if (typeof window === "undefined") {
      return;
    }

    const nextPath = settingsSectionPaths[section];
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  }, []);

  const handleBackToSettingsOverview = useCallback(() => {
    setSettingsSection(null);
    setActiveView("Settings");

    if (typeof window === "undefined") {
      return;
    }

    const nextPath = viewPaths.Settings;
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  }, []);

  const handleDismissTenantNotification = useCallback((notificationId: string) => {
    const dismissedAt = new Date().toISOString();
    setTenantNotifications(currentNotifications => currentNotifications.map(notification => (
      notification.id === notificationId ? { ...notification, dismissedAt } : notification
    )));
    updateCachedNotificationDismissal(currentBootstrapCacheScope, notificationId, dismissedAt);

    void (async () => {
      try {
        await requestJson<{ ok: boolean; dismissedAt: string }>(`/api/notifications/${encodeURIComponent(notificationId)}/dismiss`, {
          method: "POST",
        });
      } catch (error) {
        console.warn("Failed to dismiss tenant notification via API.", error);
        setTenantNotifications(currentNotifications => currentNotifications.map(notification => (
          notification.id === notificationId ? { ...notification, dismissedAt: null } : notification
        )));
        updateCachedNotificationDismissal(currentBootstrapCacheScope, notificationId, null);
      }
    })();
  }, [currentBootstrapCacheScope]);

  const handleOpenNotifications = useCallback(() => {
    handleNavigate("Notifications");
  }, [handleNavigate]);

  useEffect(() => {
    if (!authChecked || (isAuthenticated && !hasLoadedBootstrap)) {
      return;
    }

    if (allowedViewSet.has(activeView)) {
      return;
    }

    const fallbackView = availableViews[0] ?? "Dashboard";
    setActiveView(fallbackView);

    if (typeof window !== "undefined") {
      const nextPath = viewPaths[fallbackView];

      if (window.location.pathname !== nextPath) {
        window.history.replaceState({}, "", nextPath);
      }
    }
  }, [activeView, allowedViewSet, authChecked, availableViews, hasLoadedBootstrap, isAuthenticated]);

  useEffect(() => {
    if (activeView !== "Knowledge Base" || !isCreatingArticle || canCreateKbArticles) {
      return;
    }

    setIsCreatingArticle(false);
    setIsEditingKnowledgeArticle(false);

    if (typeof window !== "undefined" && window.location.pathname === knowledgeBaseNewArticlePath) {
      window.history.replaceState({}, "", viewPaths["Knowledge Base"]);
    }
  }, [activeView, canCreateKbArticles, isCreatingArticle]);

  const counts = useMemo(() => {
    const assigned = visibleTicketItems.filter(ticket => ticket.assignee === currentUserName).length;
    const unassigned = visibleTicketItems.filter(ticket => ticket.assignee === null).length;

    return {
      all: visibleTicketItems.length,
      assigned,
      unassigned,
      open: visibleTicketItems.filter(ticket => ticket.status !== "Resolved").length,
    };
  }, [currentUserName, visibleTicketItems]);

  const filteredTickets = useMemo(() => {
    return visibleTicketItems.filter(ticket => {
      const tabMatch =
        activeView !== "Tickets"
          ? true
          : activeTab === "All Tickets"
            ? true
            : activeTab === "Assigned to Me"
                ? ticket.assignee === currentUserName
              : ticket.assignee === null;

      return tabMatch;
    });
  }, [activeTab, activeView, currentUserName, visibleTicketItems]);

  const selectedTicket = filteredTickets.find(ticket => ticket.id === selectedTicketId) ?? filteredTickets[0] ?? emptyTicket;
  const selectedCustomer = customerItems.find(customer => customer.id === selectedTicket.customerId) ?? noCustomerPlaceholder;
  const selectedTicketDetail = visibleTicketItems.find(ticket => ticket.id === selectedTicketDetailId) ?? null;
  const selectedTicketDetailCustomer = selectedTicketDetail
    ? customerItems.find(customer => customer.id === selectedTicketDetail.customerId) ?? noCustomerPlaceholder
    : null;
  const selectedCustomerProfile = customerItems.find(customer => customer.id === selectedCustomerProfileId) ?? null;
  const selectedCustomerTickets = selectedCustomerProfile ? visibleTicketItems.filter(ticket => ticket.customerId === selectedCustomerProfile.id) : [];
  const relatedTickets = selectedTicketDetail
    ? selectedTicketDetail.customerId
      ? visibleTicketItems.filter(ticket => ticket.customerId === selectedTicketDetail.customerId && ticket.id !== selectedTicketDetail.id)
      : []
    : [];
  const selectedTicketActivities = selectedTicketDetail ? activityItems.filter(activity => activity.ticketId === selectedTicketDetail.id) : [];

  useEffect(() => {
    if (filteredTickets.length === 0) {
      setIsQuickViewOpen(false);
      return;
    }

    if (!filteredTickets.some(ticket => ticket.id === selectedTicketId)) {
      setSelectedTicketId(filteredTickets[0]!.id);
    }
  }, [filteredTickets, selectedTicketId]);

  const welcomeModalShownRef = useRef(false);
  useEffect(() => {
    if (activeView === "Dashboard") {
      return;
    }

    if (hasLoadedBootstrap && isAuthenticated && !workspaceSettings.welcomeShown && !welcomeModalShownRef.current) {
      const role = effectiveCurrentRole || currentUserRole;
      console.debug("[welcome] checking: role=%s, effective=%s, currentUserRole=%s, hasLoaded=%s, authed=%s, welcomeShown=%s",
        role, effectiveCurrentRole, currentUserRole, hasLoadedBootstrap, isAuthenticated, workspaceSettings.welcomeShown);
      if (role && role.toLowerCase() === "owner") {
        welcomeModalShownRef.current = true;
        setIsWelcomeModalOpen(true);
      }
    }
  }, [activeView, hasLoadedBootstrap, isAuthenticated, effectiveCurrentRole, currentUserRole, workspaceSettings.welcomeShown]);

  const handleDismissWelcomeModal = useCallback(() => {
    setIsWelcomeModalOpen(false);
    const nextSettings = { ...workspaceSettings, welcomeShown: true };
    setWorkspaceSettings(nextSettings);
    fetch("/api/settings/workspace", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(nextSettings),
    }).catch(() => {});
  }, [workspaceSettings]);

  const loadProfileApiKeys = useCallback(async (options?: { force?: boolean }) => {
    if (!authChecked || !isAuthenticated || !canManageOwnApiKeys) {
      setProfileApiKeys([]);
      setLatestGeneratedApiKey(null);
      setHasLoadedProfileApiKeys(false);
      profileApiKeysLoadPromiseRef.current = null;
      return;
    }

    if (!options?.force && hasLoadedProfileApiKeys) {
      return;
    }

    if (profileApiKeysLoadPromiseRef.current) {
      await profileApiKeysLoadPromiseRef.current;
      return;
    }

    const pendingRequest = (async () => {
      try {
        const keys = await requestJson<ApiKeySummary[]>("/api/profile/api-keys");
        setProfileApiKeys(keys);
        setHasLoadedProfileApiKeys(true);
      } catch (error) {
        setProfileApiKeys([]);
        console.warn("Failed to load profile API keys.", error);
      } finally {
        profileApiKeysLoadPromiseRef.current = null;
      }
    })();

    profileApiKeysLoadPromiseRef.current = pendingRequest;
    await pendingRequest;
  }, [authChecked, canManageOwnApiKeys, hasLoadedProfileApiKeys, isAuthenticated]);

  const refreshProfileApiKeys = async () => {
    await loadProfileApiKeys({ force: true });
  };

  useEffect(() => {
    if (authChecked && isAuthenticated && canManageOwnApiKeys) {
      return;
    }

    profileApiKeysLoadPromiseRef.current = null;
    setProfileApiKeys([]);
    setLatestGeneratedApiKey(null);
    setHasLoadedProfileApiKeys(false);
  }, [authChecked, canManageOwnApiKeys, isAuthenticated]);

  const handleGenerateApiKey = async (label: string, expiryDays: number | null) => {
    const result = await requestJson<{ apiKey: ApiKeySummary; token: string }>("/api/profile/api-keys", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ label, expiryDays }),
    });

    setLatestGeneratedApiKey(result.token);
    setHasLoadedProfileApiKeys(true);
    setProfileApiKeys(currentKeys => [result.apiKey, ...currentKeys.filter(key => key.id !== result.apiKey.id)]);
  };

  const handleRevokeApiKey = async (apiKeyId: string) => {
    await fetch(`/api/profile/api-keys/${encodeURIComponent(apiKeyId)}`, { method: "DELETE" }).then(async response => {
      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }
    });

    await refreshProfileApiKeys();
  };

  const handleProfileAvatarUpload = async (file: File) => {
    const formData = new FormData();
    formData.set("avatar", file);

    const savedProfile = await parseJsonResponse<UserProfile>(
      await fetch("/api/profile/avatar", {
        method: "POST",
        body: formData,
      }),
    );

    setCurrentUserProfile(savedProfile);
  };

  const handleProfileAvatarRemove = async () => {
    const savedProfile = await parseJsonResponse<UserProfile>(
      await fetch("/api/profile/avatar", {
        method: "DELETE",
      }),
    );

    setCurrentUserProfile(savedProfile);
  };

  const handleCustomerChange = (nextCustomer: Customer) => {
    const normalizedCustomer = syncCustomerFields(nextCustomer);
    const previousCustomer = customerItems.find(customer => customer.id === normalizedCustomer.id);

    setCustomerItems(currentCustomers => currentCustomers.map(customer => (customer.id === normalizedCustomer.id ? normalizedCustomer : customer)));

    void (async () => {
      try {
        const savedCustomer = await parseJsonResponse<Customer>(
          await fetch(`/api/customers/${encodeURIComponent(normalizedCustomer.id)}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(normalizedCustomer),
          }),
        );

        setCustomerItems(currentCustomers => currentCustomers.map(customer => (customer.id === savedCustomer.id ? syncCustomerFields(savedCustomer) : customer)));
      } catch (error) {
        console.warn("Failed to persist customer update.", error);

        if (previousCustomer) {
          setCustomerItems(currentCustomers => currentCustomers.map(customer => (customer.id === previousCustomer.id ? previousCustomer : customer)));
        }
      }
    })();
  };

  const handleTicketMutation = (ticketId: string, updates: TicketUpdateInput) => {
    const previousTicket = ticketItems.find(ticket => ticket.id === ticketId);

    if (!previousTicket) {
      return;
    }

    const { appendedNote: _appendedNote, ...ticketUpdates } = updates;

    const optimisticTicket: Ticket = {
      ...previousTicket,
      ...ticketUpdates,
      lastUpdate: new Date().toISOString(),
    };
    const mutationRequestId = nextTicketMutationRequestIdRef.current + 1;

    nextTicketMutationRequestIdRef.current = mutationRequestId;
    latestTicketMutationRequestIdsRef.current.set(ticketId, mutationRequestId);

    setTicketUpdateError(null);
    markTicketAsSyncing(ticketId);
    updateTicketSyncGuard(optimisticTicket);
    setTicketItems(currentTickets => currentTickets.map(ticket => (ticket.id === ticketId ? optimisticTicket : ticket)));

    void (async () => {
      try {
        const result = await parseJsonResponse<{ ticket: Ticket; activity: Activity | null }>(
          await fetch(`/api/tickets/${encodeURIComponent(ticketId)}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              ...optimisticTicket,
              ...(_appendedNote ? { appendedNote: _appendedNote } : {}),
            }),
          }),
        );
        const normalizedTicket = normalizeTicketLinks(result.ticket);

        if (latestTicketMutationRequestIdsRef.current.get(ticketId) !== mutationRequestId) {
          return;
        }

        setTicketUpdateError(null);
        latestTicketMutationRequestIdsRef.current.delete(ticketId);
        updateTicketSyncGuard(normalizedTicket);
        clearTicketSyncing(ticketId);
        setTicketItems(currentTickets => currentTickets.map(ticket => (ticket.id === normalizedTicket.id ? normalizedTicket : ticket)));

        if (result.activity) {
          setActivityItems(currentActivities => [result.activity!, ...currentActivities.filter(activity => activity.id !== result.activity!.id)]);
        }
      } catch (error) {
        if (latestTicketMutationRequestIdsRef.current.get(ticketId) !== mutationRequestId) {
          return;
        }

        console.warn("Failed to persist ticket update.", error);
        latestTicketMutationRequestIdsRef.current.delete(ticketId);
        clearTicketSyncGuard(ticketId);
        clearTicketSyncing(ticketId);
        setTicketUpdateError(error instanceof Error ? error.message : `Unable to update ticket ${ticketId}.`);
        setTicketItems(currentTickets => currentTickets.map(ticket => (ticket.id === previousTicket.id ? previousTicket : ticket)));
      }
    })();
  };

  const handleDeleteCustomer = (customerId: string) => {
    const customerToDelete = customerItems.find(customer => customer.id === customerId);

    if (!customerToDelete) {
      return;
    }

    if (typeof window !== "undefined") {
      const confirmed = window.confirm(`Delete ${customerToDelete.company}? This removes the account and related tickets.`);

      if (!confirmed) {
        return;
      }
    }

    const relatedTicketsToDelete = ticketItems.filter(ticket => ticket.customerId === customerId);

    setCustomerItems(currentCustomers => currentCustomers.filter(customer => customer.id !== customerId));
    setTicketItems(currentTickets => currentTickets.filter(ticket => ticket.customerId !== customerId));

    if (selectedCustomerProfileId === customerId) {
      setSelectedCustomerProfileId(null);
      setActiveView("Customers");

      if (typeof window !== "undefined" && window.location.pathname !== viewPaths.Customers) {
        window.history.pushState({}, "", viewPaths.Customers);
      }
    }

    void (async () => {
      try {
        await parseJsonResponse(
          await fetch(`/api/customers/${encodeURIComponent(customerId)}`, { method: "DELETE" }),
        );
      } catch (error) {
        console.warn("Failed to delete customer.", error);
        setCustomerItems(currentCustomers => [customerToDelete, ...currentCustomers.filter(customer => customer.id !== customerToDelete.id)]);
        setTicketItems(currentTickets => [...relatedTicketsToDelete, ...currentTickets.filter(ticket => ticket.customerId !== customerId)]);
      }
    })();
  };

  const handleCreateVendor = (input: ExpenseVendorCreateInput) => {
    const vendorId = createRecordId("vendor");
    const nextVendor: ExpenseVendor = {
      id: vendorId,
      name: input.name.trim(),
      category: input.category.trim(),
      createdAt: new Date().toISOString(),
    };

    setExpenseVendorItems(currentVendors => [nextVendor, ...currentVendors]);

    void (async () => {
      try {
        const savedVendor = await parseJsonResponse<ExpenseVendor>(
          await fetch("/api/vendors", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(nextVendor),
          }),
        );
        setExpenseVendorItems(currentVendors => currentVendors.map(vendor => (vendor.id === vendorId ? savedVendor : vendor)));
      } catch (error) {
        console.warn("Failed to create vendor.", error);
        setExpenseVendorItems(currentVendors => currentVendors.filter(vendor => vendor.id !== vendorId));
      }
    })();

    return nextVendor;
  };

  const handleUpdateVendor = (vendorId: string, input: ExpenseVendorCreateInput) => {
    const previousVendor = expenseVendorItems.find(vendor => vendor.id === vendorId);

    if (!previousVendor) {
      return;
    }

    const nextVendor: ExpenseVendor = {
      ...previousVendor,
      name: input.name.trim(),
      category: input.category.trim(),
    };

    setExpenseVendorItems(currentVendors => currentVendors.map(vendor => (vendor.id === vendorId ? nextVendor : vendor)));

    void (async () => {
      try {
        const savedVendor = await parseJsonResponse<ExpenseVendor>(
          await fetch(`/api/vendors/${encodeURIComponent(vendorId)}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ name: nextVendor.name, category: nextVendor.category }),
          }),
        );
        setExpenseVendorItems(currentVendors => currentVendors.map(vendor => (vendor.id === vendorId ? savedVendor : vendor)));
      } catch (error) {
        console.warn("Failed to update vendor.", error);
        setExpenseVendorItems(currentVendors => currentVendors.map(vendor => (vendor.id === vendorId ? previousVendor : vendor)));
      }
    })();
  };

  const handleDeleteVendor = (vendorId: string) => {
    const vendorToDelete = expenseVendorItems.find(vendor => vendor.id === vendorId);

    if (!vendorToDelete) {
      return;
    }

    if (typeof window !== "undefined") {
      const confirmed = window.confirm(`Remove ${vendorToDelete.name}? This also deletes its expenses.`);

      if (!confirmed) {
        return;
      }
    }

    const relatedExpensesToDelete = expenseItems.filter(expense => expense.vendorId === vendorId);

    setExpenseVendorItems(currentVendors => currentVendors.filter(vendor => vendor.id !== vendorId));
    setExpenseItems(currentExpenses => currentExpenses.filter(expense => expense.vendorId !== vendorId));

    void (async () => {
      try {
        await parseJsonResponse(
          await fetch(`/api/vendors/${encodeURIComponent(vendorId)}`, { method: "DELETE" }),
        );
      } catch (error) {
        console.warn("Failed to delete vendor.", error);
        setExpenseVendorItems(currentVendors => [vendorToDelete, ...currentVendors.filter(vendor => vendor.id !== vendorId)]);
        setExpenseItems(currentExpenses => [...relatedExpensesToDelete, ...currentExpenses.filter(expense => expense.vendorId !== vendorId)]);
      }
    })();
  };

  const handleCreateExpense = (input: ExpenseCreateInput, invoices: File[] = []) => {
    const expenseId = createRecordId("expense");
    const timestamp = new Date().toISOString();
    const nextExpense: Expense = {
      id: expenseId,
      vendorId: input.vendorId,
      customerId: input.customerId ?? null,
      description: input.description.trim(),
      amount: input.amount,
      expenseDate: input.expenseDate,
      createdBy: currentKnowledgeUserEmail,
      invoices: [],
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    setExpenseItems(currentExpenses => [nextExpense, ...currentExpenses]);

    return (async () => {
      try {
        const savedExpense = await parseJsonResponse<Expense>(
          await fetch("/api/expenses", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(nextExpense),
          }),
        );

        let mergedExpense = savedExpense;

        if (invoices.length > 0) {
          try {
            const formData = new FormData();
            for (const file of invoices) {
              formData.append("files", file);
            }
            const uploadResult = await parseJsonResponse<{ invoices: ExpenseInvoice[] }>(
              await fetch(`/api/expenses/${encodeURIComponent(savedExpense.id)}/invoices`, {
                method: "POST",
                body: formData,
              }),
            );
            mergedExpense = { ...savedExpense, invoices: uploadResult.invoices ?? [] };
          } catch (uploadError) {
            console.warn("Failed to upload expense invoices.", uploadError);
          }
        }

        setExpenseItems(currentExpenses => currentExpenses.map(expense => (expense.id === expenseId ? mergedExpense : expense)));
      } catch (error) {
        console.warn("Failed to create expense.", error);
        setExpenseItems(currentExpenses => currentExpenses.filter(expense => expense.id !== expenseId));
      }
    })();
  };

  const handleUpdateExpense = (expenseId: string, input: ExpenseCreateInput, invoices: File[] = []) => {
    const previousExpense = expenseItems.find(expense => expense.id === expenseId);

    if (!previousExpense) {
      return Promise.resolve();
    }

    const optimisticExpense: Expense = {
      ...previousExpense,
      vendorId: input.vendorId,
      customerId: input.customerId ?? null,
      description: input.description.trim(),
      amount: input.amount,
      expenseDate: input.expenseDate,
      updatedAt: new Date().toISOString(),
    };

    setExpenseItems(currentExpenses => currentExpenses.map(expense => (expense.id === expenseId ? optimisticExpense : expense)));

    return (async () => {
      try {
        const savedExpense = await parseJsonResponse<Expense>(
          await fetch(`/api/expenses/${encodeURIComponent(expenseId)}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              vendorId: input.vendorId,
              customerId: input.customerId ?? null,
              description: input.description.trim(),
              amount: input.amount,
              expenseDate: input.expenseDate,
            }),
          }),
        );

        let mergedInvoices = savedExpense.invoices ?? previousExpense.invoices ?? [];

        if (invoices.length > 0) {
          try {
            const formData = new FormData();
            for (const file of invoices) {
              formData.append("files", file);
            }
            const uploadResult = await parseJsonResponse<{ invoices: ExpenseInvoice[] }>(
              await fetch(`/api/expenses/${encodeURIComponent(expenseId)}/invoices`, {
                method: "POST",
                body: formData,
              }),
            );
            mergedInvoices = uploadResult.invoices ?? mergedInvoices;
          } catch (uploadError) {
            console.warn("Failed to upload expense invoices.", uploadError);
          }
        }

        setExpenseItems(currentExpenses => currentExpenses.map(expense => (
          expense.id === expenseId ? { ...savedExpense, invoices: mergedInvoices } : expense
        )));
      } catch (error) {
        console.warn("Failed to update expense.", error);
        setExpenseItems(currentExpenses => currentExpenses.map(expense => (expense.id === expenseId ? previousExpense : expense)));
      }
    })();
  };

  const handleDeleteExpenseInvoice = (expenseId: string, invoiceKey: string) => {
    const expenseSnapshot = expenseItems.find(expense => expense.id === expenseId);
    const invoiceSnapshot = expenseSnapshot?.invoices?.find(invoice => invoice.key === invoiceKey);

    if (!expenseSnapshot || !invoiceSnapshot) {
      return;
    }

    setExpenseItems(currentExpenses => currentExpenses.map(expense => (
      expense.id === expenseId
        ? { ...expense, invoices: (expense.invoices ?? []).filter(invoice => invoice.key !== invoiceKey) }
        : expense
    )));

    void (async () => {
      try {
        await fetch(`/api/expenses/${encodeURIComponent(expenseId)}/invoices/${encodeURIComponent(invoiceKey)}`, {
          method: "DELETE",
        });
      } catch (error) {
        console.warn("Failed to delete expense invoice.", error);
        setExpenseItems(currentExpenses => currentExpenses.map(expense => (
          expense.id === expenseId
            ? { ...expense, invoices: [invoiceSnapshot, ...(expense.invoices ?? []).filter(invoice => invoice.key !== invoiceKey)] }
            : expense
        )));
      }
    })();
  };

  const handleDeleteExpense = (expenseId: string) => {
    const expenseToDelete = expenseItems.find(expense => expense.id === expenseId);

    if (!expenseToDelete) {
      return;
    }

    setExpenseItems(currentExpenses => currentExpenses.filter(expense => expense.id !== expenseId));

    void (async () => {
      try {
        await parseJsonResponse(
          await fetch(`/api/expenses/${encodeURIComponent(expenseId)}`, { method: "DELETE" }),
        );
      } catch (error) {
        console.warn("Failed to delete expense.", error);
        setExpenseItems(currentExpenses => [expenseToDelete, ...currentExpenses.filter(expense => expense.id !== expenseId)]);
      }
    })();
  };

  const handleDeleteTicket = (ticketId: string) => {
    const ticketToDelete = ticketItems.find(ticket => ticket.id === ticketId);

    if (!ticketToDelete) {
      return;
    }

    if (typeof window !== "undefined") {
      const ticketLabel = ticketToDelete.subject?.trim() ? `${ticketToDelete.id} — ${ticketToDelete.subject.trim()}` : ticketToDelete.id;
      const confirmed = window.confirm(`Delete ${ticketLabel}? This permanently removes the ticket.`);

      if (!confirmed) {
        return;
      }
    }

    const relatedActivitiesToRestore = activityItems.filter(activity => activity.ticketId === ticketId);

    deletedTicketIdsRef.current.add(ticketId);
    clearTicketSyncGuard(ticketId);
    latestTicketMutationRequestIdsRef.current.delete(ticketId);
    requestedTicketAttachmentIdsRef.current.delete(ticketId);

    setTicketItems(currentTickets => currentTickets.filter(ticket => ticket.id !== ticketId));
    setActivityItems(currentActivities => currentActivities.filter(activity => activity.ticketId !== ticketId));
    setLoadingTicketAttachmentIds(currentTicketIds => currentTicketIds.filter(currentTicketId => currentTicketId !== ticketId));
    setSyncingTicketIds(currentTicketIds => currentTicketIds.filter(currentTicketId => currentTicketId !== ticketId));
    setSelectedTicketId(current => (current === ticketId ? "" : current));

    if (selectedTicketDetailId === ticketId) {
      setSelectedTicketDetailId(null);
      setActiveView("Tickets");

      if (typeof window !== "undefined" && window.location.pathname !== viewPaths.Tickets) {
        window.history.pushState({}, "", viewPaths.Tickets);
      }
    }

    void (async () => {
      try {
        const response = await fetch(`/api/tickets/${encodeURIComponent(ticketId)}`, { method: "DELETE" });

        if (!response.ok) {
          throw new Error(`Failed to delete ticket (status ${response.status}).`);
        }
      } catch (error) {
        console.warn("Failed to delete ticket.", error);
        deletedTicketIdsRef.current.delete(ticketId);
        setTicketItems(currentTickets => [ticketToDelete, ...currentTickets.filter(ticket => ticket.id !== ticketId)]);
        setActivityItems(currentActivities => [...relatedActivitiesToRestore, ...currentActivities.filter(activity => activity.ticketId !== ticketId)]);
      }
    })();
  };

  const handleCreateCustomer = (input: CustomerCreateInput) => {
    const customerId = createRecordId("cust");
    const primaryContactId = createRecordId(`${customerId}-contact`);
    const defaultRenewal = new Date();

    defaultRenewal.setMonth(defaultRenewal.getMonth() + 12);

    const nextRenewalDate = formatDateLabel(
      `${defaultRenewal.getFullYear()}-${String(defaultRenewal.getMonth() + 1).padStart(2, "0")}-${String(defaultRenewal.getDate()).padStart(2, "0")}`,
    );

    const nextCustomer = syncCustomerFields(
      appendCustomerActionHistory(
        {
          id: customerId,
          name: input.primaryContactName,
          initials: "",
          company: input.company,
          email: input.email,
          phone: input.phone,
          region: input.region,
          health: input.health,
          accountStatus: input.health === "Disabled" ? "Disabled" : "Active",
          renewalDate: nextRenewalDate,
          contacts: [
            {
              id: primaryContactId,
              role: "Primary",
              name: input.primaryContactName,
              email: input.email,
              phone: input.phone || undefined,
            },
          ],
          products: [],
          notes: [],
          actionHistory: [],
          secondaryContact: {
            name: "",
            email: "",
          },
        },
        {
          entity: "Account status",
          action: "Created account",
          detail: `${input.company} was added to the customer directory.`,
          actor: currentUserName,
        },
      ),
    );

    setCustomerItems(currentCustomers => [nextCustomer, ...currentCustomers]);
    setActiveView("Customers");
    setSelectedCustomerProfileId(nextCustomer.id);

    void (async () => {
      try {
        const savedCustomer = await parseJsonResponse<Customer>(
          await fetch("/api/customers", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(nextCustomer),
          }),
        );

        setCustomerItems(currentCustomers => [syncCustomerFields(savedCustomer), ...currentCustomers.filter(customer => customer.id !== savedCustomer.id)]);
      } catch (error) {
        console.warn("Failed to create customer via API.", error);
      }
    })();

    if (typeof window !== "undefined") {
      const nextPath = `${viewPaths.Customers}/${encodeURIComponent(nextCustomer.id)}`;
      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const handleTicketPriorityChange = (ticketId: string, priority: TicketPriority) => {
    handleTicketMutation(ticketId, { priority });
  };

  const handleTicketStatusChange = (ticketId: string, status: TicketStatus) => {
    handleTicketMutation(ticketId, { status });
  };

  const handleTicketCategoryChange = (ticketId: string, category: string) => {
    handleTicketMutation(ticketId, { category });
  };

  const handleTicketProductChange = (ticketId: string, product: string) => {
    handleTicketMutation(ticketId, { product });
  };

  const handleTicketAssignmentChange = (ticketId: string, assignee: string | null) => {
    handleTicketMutation(ticketId, { assignee });
  };

  const handleTicketScheduleChange = (ticketId: string, scheduleStartAt: string | null, scheduleAt: string | null) => {
    handleTicketMutation(ticketId, { scheduleStartAt, scheduleAt });
  };

  const handleTicketCustomerChange = (ticketId: string, customerId: string) => {
    const previousTicket = ticketItems.find(ticket => ticket.id === ticketId);

    if (!previousTicket) {
      return;
    }

    const nextCustomer = customerItems.find(customer => customer.id === customerId) ?? null;
    const nextProduct = nextCustomer
      ? (hasConfiguredTicketProduct(previousTicket.product) ? previousTicket.product : getDefaultTicketProduct(nextCustomer))
      : previousTicket.product;

    handleTicketMutation(ticketId, {
      customerId,
      plan: nextCustomer ? getTicketPlanLabel(nextCustomer) : "None",
      product: nextProduct,
    });
  };

  const handleCreateTicket = async (input: TicketCreateInput) => {
    const customer = customerItems.find(item => item.id === input.customerId) ?? null;
    const categoryName = input.category.trim();
    const productName = input.product.trim();

    if (!categoryName || !productName) {
      return;
    }

    let allocatedTicketId: string;

    try {
      const allocation = await parseJsonResponse<{ id: string }>(
        await fetch("/api/tickets/allocate-id", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ category: categoryName }),
        }),
      );
      allocatedTicketId = allocation.id;
    } catch (error) {
      console.warn("Failed to allocate a ticket id.", error);
      return;
    }

    const nextTicket: Ticket = {
      id: allocatedTicketId,
      subject: input.subject.trim(),
      description: input.description.trim(),
      category: categoryName,
      product: productName,
      sla: getTicketSlaLabel(customer, productName, productPlanItems),
      customerId: customer?.id ?? "",
      plan: customer ? getTicketPlanLabel(customer) : "None",
      priority: input.priority,
      status: input.status,
      createdAt: new Date().toISOString(),
      lastUpdate: new Date().toISOString(),
      scheduleStartAt: input.scheduleStartAt,
      scheduleAt: input.scheduleAt,
      slaPausedAt: null,
      slaPausedTotalMs: 0,
      assignee: input.assignee,
      notes: "",
      linkedArticleIds: [],
      customFieldValues: normalizeTicketCustomFieldValues(input.customFieldValues ?? {}),
      attachments: [],
    };

    setTicketItems(currentTickets => [nextTicket, ...currentTickets]);
  markTicketAsSyncing(nextTicket.id);
  updateTicketSyncGuard(nextTicket);
    setSelectedTicketId(nextTicket.id);
    setSelectedTicketDetailId(nextTicket.id);
    setIsQuickViewOpen(false);
    setActiveView("Tickets");
    pendingTicketCreationIdsRef.current.add(nextTicket.id);

    if (typeof window !== "undefined") {
      const nextPath = `${viewPaths.Tickets}/${encodeURIComponent(nextTicket.id)}`;

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }

    void (async () => {
      try {
        const result = await parseJsonResponse<{ ticket: Ticket; activity: Activity | null }>(
          await fetch("/api/tickets", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(nextTicket),
          }),
        );
        const normalizedTicket = normalizeTicketLinks(result.ticket);
        pendingTicketCreationIdsRef.current.delete(nextTicket.id);
        clearTicketSyncing(nextTicket.id);
        updateTicketSyncGuard(normalizedTicket);

        setTicketItems(currentTickets => [normalizedTicket, ...currentTickets.filter(ticket => ticket.id !== normalizedTicket.id)]);

        if (result.activity) {
          setActivityItems(currentActivities => [result.activity!, ...currentActivities.filter(activity => activity.id !== result.activity!.id)]);
        }
      } catch (error) {
        pendingTicketCreationIdsRef.current.delete(nextTicket.id);
        console.warn("Failed to create ticket via API.", error);
        clearTicketSyncGuard(nextTicket.id);
        clearTicketSyncing(nextTicket.id);
        setTicketItems(currentTickets => currentTickets.filter(ticket => ticket.id !== nextTicket.id));
        requestedTicketAttachmentIdsRef.current.delete(nextTicket.id);
        setLoadingTicketAttachmentIds(currentTicketIds => currentTicketIds.filter(ticketId => ticketId !== nextTicket.id));
        if (selectedTicketId === nextTicket.id) {
          setSelectedTicketId("");
        }
        if (selectedTicketDetailId === nextTicket.id) {
          setSelectedTicketDetailId(null);
        }
        if (typeof window !== "undefined" && window.location.pathname === `${viewPaths.Tickets}/${encodeURIComponent(nextTicket.id)}`) {
          window.history.pushState({}, "", viewPaths.Tickets);
        }
      }
    })();
  };

  const handleSaveKnowledgeArticle = async (input: KnowledgeArticleCreateInput, articleId?: string) => {
    const savedArticle = await parseJsonResponse<KnowledgeArticle>(
      await fetch(articleId ? `/api/knowledge-base/articles/${encodeURIComponent(articleId)}` : "/api/knowledge-base/articles", {
        method: articleId ? "PUT" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    );

    const refreshedKnowledgeBase = await parseJsonResponse<KnowledgeBaseSnapshot>(await fetch("/api/knowledge-base"));
    setKnowledgeBase(refreshedKnowledgeBase);

    return refreshedKnowledgeBase.articles.find(article => article.id === savedArticle.id) ?? savedArticle;
  };

  const handleTicketAttachmentUpload = async (ticketId: string, files: FileList | File[], onProgress?: (percent: number) => void) => {
    const selectedFiles = Array.from(files).filter(file => file.size > 0);

    if (selectedFiles.length === 0) {
      return;
    }

    const formData = new FormData();

    for (const file of selectedFiles) {
      formData.append("files", file);
    }

    const result = await uploadFormDataWithProgress<{ attachments: TicketAttachment[]; activity: Activity | null }>(
      `/api/tickets/${encodeURIComponent(ticketId)}/attachments`,
      formData,
      onProgress,
    );

    requestedTicketAttachmentIdsRef.current.add(ticketId);

    setTicketItems(currentTickets => currentTickets.map(ticket => (
      ticket.id === ticketId
        ? updateTicketSyncGuard(normalizeTicketLinks({ ...ticket, attachments: result.attachments }))
        : ticket
    )));

    if (result.activity) {
      setActivityItems(currentActivities => [result.activity!, ...currentActivities.filter(activity => activity.id !== result.activity!.id)]);
    }
  };

  const handleTicketAttachmentDelete = async (ticketId: string, attachmentKey: string) => {
    const result = await requestJson<{ attachments: TicketAttachment[]; activity: Activity | null }>(
      `/api/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentKey)}`,
      {
        method: "DELETE",
      },
    );

    requestedTicketAttachmentIdsRef.current.add(ticketId);

    setTicketItems(currentTickets => currentTickets.map(ticket => (
      ticket.id === ticketId
        ? updateTicketSyncGuard(normalizeTicketLinks({ ...ticket, attachments: result.attachments }))
        : ticket
    )));

    if (result.activity) {
      setActivityItems(currentActivities => [result.activity!, ...currentActivities.filter(activity => activity.id !== result.activity!.id)]);
    }
  };

  const handleTicketAttachmentShareChange = async (ticketId: string, attachmentKey: string, override: TicketAttachmentPublicShare | null) => {
    const result = await requestJson<{ attachments: TicketAttachment[]; activity: Activity | null }>(
      `/api/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentKey)}/share`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ override }),
      },
    );

    requestedTicketAttachmentIdsRef.current.add(ticketId);

    setTicketItems(currentTickets => currentTickets.map(ticket => (
      ticket.id === ticketId
        ? updateTicketSyncGuard(normalizeTicketLinks({ ...ticket, attachments: result.attachments }))
        : ticket
    )));

    if (result.activity) {
      setActivityItems(currentActivities => [result.activity!, ...currentActivities.filter(activity => activity.id !== result.activity!.id)]);
    }
  };

  const handleCreateKnowledgeCollection = async (input: KnowledgeCollectionCreateInput) => {
    const savedCollection = await parseJsonResponse<KnowledgeCollection>(
      await fetch("/api/knowledge-base/collections", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    );

    setKnowledgeBase(currentKnowledgeBase => ({
      ...currentKnowledgeBase,
      collections: [...currentKnowledgeBase.collections, savedCollection].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name)),
    }));
    setActiveView("Knowledge Base");
    setIsCreatingArticle(false);
    setSelectedKnowledgeCollectionId(savedCollection.id);

    if (typeof window !== "undefined") {
      const nextPath = getKnowledgeBaseCollectionPath(savedCollection.id);

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const handleDeleteKnowledgeArticle = async (articleId: string, collectionId?: string | null) => {
    const response = await fetch(`/api/knowledge-base/articles/${encodeURIComponent(articleId)}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete article (status ${response.status}).`);
    }

    const refreshedKnowledgeBase = await parseJsonResponse<KnowledgeBaseSnapshot>(await fetch("/api/knowledge-base"));
    setKnowledgeBase(refreshedKnowledgeBase);
    setActiveView("Knowledge Base");
    setIsCreatingArticle(false);
    setIsEditingKnowledgeArticle(false);
    setSelectedKnowledgeArticleId(null);

    const nextCollectionId = collectionId && refreshedKnowledgeBase.collections.some(collection => collection.id === collectionId) ? collectionId : null;
    setSelectedKnowledgeCollectionId(nextCollectionId);

    if (typeof window !== "undefined") {
      const nextPath = nextCollectionId ? getKnowledgeBaseCollectionPath(nextCollectionId) : viewPaths["Knowledge Base"];

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const handleDeleteKnowledgeCollection = async (collectionId: string) => {
    const response = await fetch(`/api/knowledge-base/collections/${encodeURIComponent(collectionId)}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete collection (status ${response.status}).`);
    }

    const refreshedKnowledgeBase = await parseJsonResponse<KnowledgeBaseSnapshot>(await fetch("/api/knowledge-base"));
    setKnowledgeBase(refreshedKnowledgeBase);
    setActiveView("Knowledge Base");
    setIsCreatingArticle(false);
    setIsEditingKnowledgeArticle(false);
    setSelectedKnowledgeArticleId(null);
    setSelectedKnowledgeCollectionId(null);

    if (typeof window !== "undefined" && window.location.pathname !== viewPaths["Knowledge Base"]) {
      window.history.pushState({}, "", viewPaths["Knowledge Base"]);
    }
  };

  const handleToggleKnowledgeArticleFeatured = async (article: KnowledgeArticle) => {
    await handleSaveKnowledgeArticle(
      {
        title: article.title,
        summary: article.summary,
        category: selectedKnowledgeArticleCollection?.name,
        collectionId: article.collectionId,
        visibility: article.visibility,
        ownerUserEmail: article.ownerUserEmail,
        sharedWithUserEmails: article.sharedWithUserEmails,
        audience: article.audience,
        status: article.status,
        estimatedReadTime: article.estimatedReadTime,
        keywords: article.keywords,
        content: article.content,
        isFeatured: !article.isFeatured,
        isPopular: article.isPopular,
      },
      article.id,
    );
  };

  const handlePrimaryColorChange = (primaryColor: string) => {
    setAppBranding(current => {
      const nextBranding = {
        ...current,
        primaryColor: normalizeHexColor(primaryColor, current.primaryColor),
      };

      void (async () => {
        try {
          const savedBranding = await parseJsonResponse<AppBranding>(
            await fetch("/api/settings/branding", {
              method: "PUT",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(nextBranding),
            }),
          );

          setAppBranding(savedBranding);
        } catch (error) {
          console.warn("Failed to persist branding color change.", error);
        }
      })();

      return nextBranding;
    });
  };

  const handleConsoleNameChange = (consoleName: string) => {
    setAppBranding(current => {
      const trimmed = consoleName.trim();
      const nextBranding = {
        ...current,
        consoleName: trimmed.length > 0 ? trimmed : null,
      };

      void (async () => {
        try {
          const savedBranding = await parseJsonResponse<AppBranding>(
            await fetch("/api/settings/branding", {
              method: "PUT",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(nextBranding),
            }),
          );

          setAppBranding(savedBranding);
        } catch (error) {
          console.warn("Failed to persist console name change.", error);
        }
      })();

      return nextBranding;
    });
  };

  const handleLogoUpload = (file: File | null) => {
    if (!file) {
      setAppBranding(current => {
        const nextBranding = { ...current, logoDataUrl: null, logoName: null };

        void (async () => {
          try {
            const savedBranding = await parseJsonResponse<AppBranding>(
              await fetch("/api/settings/branding", {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(nextBranding),
              }),
            );

            setAppBranding(savedBranding);
          } catch (error) {
            console.warn("Failed to remove branding logo.", error);
          }
        })();

        return nextBranding;
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const logoDataUrl = typeof reader.result === "string" ? reader.result : null;

      if (!logoDataUrl) {
        return;
      }

      setAppBranding(current => {
        const nextBranding = {
          ...current,
          logoDataUrl,
          logoName: file.name,
        };

        void (async () => {
          try {
            const savedBranding = await parseJsonResponse<AppBranding>(
              await fetch("/api/settings/branding", {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(nextBranding),
              }),
            );

            setAppBranding(savedBranding);
          } catch (error) {
            console.warn("Failed to persist branding logo.", error);
          }
        })();

        return nextBranding;
      });
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setAppBranding(current => {
      const nextBranding = { ...current, logoDataUrl: null, logoName: null };

      void (async () => {
        try {
          const savedBranding = await parseJsonResponse<AppBranding>(
            await fetch("/api/settings/branding", {
              method: "PUT",
              headers: { "content-type": "application/json" },
              body: JSON.stringify(nextBranding),
            }),
          );

          setAppBranding(savedBranding);
        } catch (error) {
          console.warn("Failed to persist branding logo removal.", error);
        }
      })();

      return nextBranding;
    });
  };

  const handleWorkspaceSettingsChange = (nextWorkspaceSettings: WorkspaceSettings) => {
    const previousWorkspaceSettings = workspaceSettings;
    const routingChanged = JSON.stringify(nextWorkspaceSettings.caseEmailRoutes) !== JSON.stringify(previousWorkspaceSettings.caseEmailRoutes);
    setWorkspaceSettings(nextWorkspaceSettings);

    if (routingChanged) {
      setEmailRoutingSyncState("syncing");
      setEmailRoutingSyncMessage("Syncing mailbox routes…");
    }

    void (async () => {
      try {
        const savedWorkspaceSettings = await parseJsonResponse<WorkspaceSettings>(
          await fetch("/api/settings/workspace", {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(nextWorkspaceSettings),
          }),
        );

        setWorkspaceSettings({ ...defaultWorkspaceSettings, ...savedWorkspaceSettings });

        if (routingChanged) {
          setEmailRoutingSyncState("success");
          setEmailRoutingSyncMessage("Mailbox routes updated just now.");
        }
      } catch (error) {
        console.warn("Failed to persist workspace settings.", error);
        setWorkspaceSettings(previousWorkspaceSettings);

        if (routingChanged) {
          setEmailRoutingSyncState("error");
          setEmailRoutingSyncMessage(error instanceof Error ? error.message : "Mailbox route update failed.");
        }
      }
    })();
  };

  const handleRefreshWorkspaceSettings = useCallback(async () => {
    const savedWorkspaceSettings = await parseJsonResponse<WorkspaceSettings>(
      await fetch("/api/settings/workspace", {
        method: "GET",
        headers: { accept: "application/json" },
      }),
    );

    setWorkspaceSettings({ ...defaultWorkspaceSettings, ...savedWorkspaceSettings });
  }, []);

  const handleToggleCaseEmailRouteOutbound = async (routeId: string, enabled: boolean) => {
    const payload = await parseJsonResponse<{
      outbound?: {
        enabled?: boolean;
        verifiedForSending?: boolean;
        verificationStatus?: "disabled" | "pending" | "ready" | "error";
        verificationError?: string | null;
        verifiedAt?: string | null;
      };
      workspaceSettings?: WorkspaceSettings;
    }>(
      await fetch("/api/settings/email-outbound", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ routeId, enabled }),
      }),
    );

    if (payload.workspaceSettings) {
      setWorkspaceSettings({ ...defaultWorkspaceSettings, ...payload.workspaceSettings });
    }

    return {
      enabled: Boolean(payload.outbound?.enabled ?? enabled),
      verifiedForSending: Boolean(payload.outbound?.verifiedForSending),
      verificationStatus: payload.outbound?.verificationStatus ?? (enabled ? "pending" : "disabled"),
      verificationError: payload.outbound?.verificationError ?? null,
      verifiedAt: payload.outbound?.verifiedAt ?? null,
    };
  };

  const handleRefreshCaseEmailRouteOutboundStatuses = useCallback(async (routeId?: string) => {
    const payload = await parseJsonResponse<{
      workspaceSettings?: WorkspaceSettings;
    }>(
      await fetch("/api/settings/email-outbound/status", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(routeId ? { routeId } : {}),
      }),
    );

    if (payload.workspaceSettings) {
      setWorkspaceSettings({ ...defaultWorkspaceSettings, ...payload.workspaceSettings });
    }
  }, []);

  const handleRestoreTenantDatabase = useCallback(async (restoreTimestamp: string) => {
    const payload = await parseJsonResponse<RestoreTenantDatabaseResponse>(
      await fetch("/api/settings/database/restore", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ restoreTimestamp }),
      }),
    );

    return {
      message: payload.restore?.message ?? "Database restored successfully.",
      restoreDate: payload.restore?.restoreDate ?? restoreTimestamp.slice(0, 10),
      restoreTimestamp: payload.restore?.restoreTimestamp ?? restoreTimestamp,
      retentionDays: payload.restore?.retentionDays ?? null,
    };
  }, []);

  const handleLoadTenantRestorePoints = useCallback(async () => {
    const payload = await parseJsonResponse<TenantDatabaseRestorePointsResponse>(
      await fetch("/api/settings/database/restore-points", {
        method: "GET",
      }),
    );

    return {
      retentionDays: payload.retentionDays ?? null,
      earliestRestoreTimestamp: payload.earliestRestoreTimestamp ?? null,
      latestRestoreTimestamp: payload.latestRestoreTimestamp ?? null,
      earliestRestoreDate: payload.earliestRestoreDate ?? null,
      latestRestoreDate: payload.latestRestoreDate ?? null,
    };
  }, []);

  const navigateToTenantAuthRoute = (route: TenantAuthRoute) => {
    if (typeof window === "undefined") {
      return;
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.pathname = route === "reset-password" ? "/reset-password" : "/";
    nextUrl.search = "";
    window.history.pushState({}, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setTenantAuthRoute(route);
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setResetRequestError(null);
    setResetRequestNotice(null);
    setResetSubmitError(null);
    setResetSubmitNotice(null);
    setAuthError(null);
  };

  const handleRequestPasswordReset = async () => {
    const normalizedEmail = loginEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setResetRequestError("Enter your email address.");
      setResetRequestNotice(null);
      return;
    }

    setIsRequestingPasswordReset(true);
    setResetRequestError(null);
    setResetRequestNotice(null);
    setResetSubmitError(null);

    try {
      const payload = await requestJson<{ ok: boolean; message?: string }>("/api/auth/password-reset/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      setResetRequestNotice(payload.message ?? "If this email exists in our system, check your email for the reset link.");
    } catch (error) {
      setResetRequestError(error instanceof Error ? error.message : "Unable to send the password reset email.");
    } finally {
      setIsRequestingPasswordReset(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetToken) {
      setResetSubmitError("This password reset link is missing or invalid.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setResetSubmitError("Enter and confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetSubmitError("Your passwords do not match.");
      return;
    }

    setIsSubmittingPasswordReset(true);
    setResetSubmitError(null);
    setResetSubmitNotice(null);

    try {
      await requestJson<{ ok: boolean }>("/api/auth/password-reset/confirm", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token: resetToken,
          newPassword,
        }),
      });

      setNewPassword("");
      setConfirmPassword("");
      setResetToken("");
      navigateToTenantAuthRoute("login");
    } catch (error) {
      setResetSubmitError(error instanceof Error ? error.message : "Unable to reset your password.");
    } finally {
      setIsSubmittingPasswordReset(false);
    }
  };

  const selectedKnowledgeCollection = selectedKnowledgeCollectionId
    ? visibleKnowledgeBase.collections.find(collection => collection.id === selectedKnowledgeCollectionId) ?? null
    : null;
  const selectedKnowledgeCollectionArticles = selectedKnowledgeCollection
    ? visibleKnowledgeBase.articles.filter(article => article.collectionId === selectedKnowledgeCollection.id)
    : [];
  const selectedKnowledgeArticle = selectedKnowledgeArticleId
    ? visibleKnowledgeBase.articles.find(article => article.id === selectedKnowledgeArticleId) ?? null
    : null;
  const canManageSelectedKnowledgeArticle = selectedKnowledgeArticle
    ? selectedKnowledgeArticle.visibility === "private"
      ? selectedKnowledgeArticle.ownerUserEmail === currentKnowledgeUserEmail
      : canManageKbContent
    : false;
  const selectedKnowledgeArticleCollection = selectedKnowledgeArticle?.collectionId
    ? visibleKnowledgeBase.collections.find(collection => collection.id === selectedKnowledgeArticle.collectionId) ?? null
    : null;

  useEffect(() => {
    if (activeView !== "Knowledge Base" || isCreatingArticle || isEditingKnowledgeArticle || !selectedKnowledgeArticle) {
      lastTrackedKnowledgeArticleIdRef.current = null;
      return;
    }

    if (lastTrackedKnowledgeArticleIdRef.current === selectedKnowledgeArticle.id) {
      return;
    }

    lastTrackedKnowledgeArticleIdRef.current = selectedKnowledgeArticle.id;

    void (async () => {
      try {
        const refreshedKnowledgeBase = await parseJsonResponse<KnowledgeBaseSnapshot>(
          await fetch(`/api/knowledge-base/articles/${encodeURIComponent(selectedKnowledgeArticle.id)}/view`, {
            method: "POST",
          }),
        );

        setKnowledgeBase(refreshedKnowledgeBase);
      } catch (error) {
        console.warn("Failed to record knowledge article view.", error);
      }
    })();
  }, [activeView, isCreatingArticle, isEditingKnowledgeArticle, selectedKnowledgeArticle]);
  const globalSearchCategoryOptions = useMemo(
    () => [...new Set(visibleTicketCategories.map(category => category.name).filter(Boolean))].sort((left, right) => left.localeCompare(right)),
    [visibleTicketCategories],
  );

  useEffect(() => {
    const normalizedQuery = globalSearchQuery.trim();

    if (!normalizedQuery || !authChecked || !isAuthenticated) {
      setGlobalSearchResults([]);
      setIsGlobalSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    const debounceTimeoutId = window.setTimeout(() => {
      setIsGlobalSearchLoading(true);
      void (async () => {
        try {
          const params = new URLSearchParams({ q: normalizedQuery });

          if (profileTimezone) {
            params.set("tz", profileTimezone);
          }

          if (globalSearchCategory && globalSearchCategory !== "all") {
            params.set("category", globalSearchCategory);
          }

          const response = await requestJson<{ results: GlobalSearchResult[] }>(`/api/search?${params.toString()}`, {
            signal: controller.signal,
          });

          setGlobalSearchResults(response.results ?? []);
        } catch (error) {
          if ((error as Error).name === "AbortError") {
            return;
          }

          console.warn("Failed to run global search.", error);
        } finally {
          if (!controller.signal.aborted) {
            setIsGlobalSearchLoading(false);
          }
        }
      })();
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(debounceTimeoutId);
    };
  }, [authChecked, globalSearchCategory, globalSearchQuery, isAuthenticated, profileTimezone]);

  const loadRolePresets = useCallback(() => requestJson<RolePreset[]>("/api/roles"), []);

  const applyWorkspaceMemberUpsert = (member: WorkspaceMember | null | undefined) => {
    if (!member) {
      return;
    }

    setWorkspaceMembers(current => sortWorkspaceMembers([
      ...current.filter(entry => entry.email.toLowerCase() !== member.email.toLowerCase()),
      member,
    ]));

    if (currentUserEmail && member.email.toLowerCase() === currentUserEmail.toLowerCase()) {
      setCurrentUserRole(member.role);
    }
  };

  const handleCreateWorkspaceMember = async (input: { name: string; email: string; role: string; password: string; resetPasswordOnLogin?: boolean; allowedCategoryIds: string[] | null; teamIds: string[] }) => {
    const response = await requestJson<{ member?: WorkspaceMember }>("/api/team-members", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });

    applyWorkspaceMemberUpsert(response.member);
  };

  const handleUpdateWorkspaceMember = async (email: string, input: { name: string; role: string; allowedCategoryIds: string[] | null; teamIds: string[]; status?: "enabled" | "disabled" }) => {
    const response = await requestJson<{ member?: WorkspaceMember }>(`/api/team-members/${encodeURIComponent(email)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });

    applyWorkspaceMemberUpsert(response.member);
  };

  const handleDeleteWorkspaceMember = async (email: string) => {
    await requestJson<{ ok: boolean }>(`/api/team-members/${encodeURIComponent(email)}`, {
      method: "DELETE",
    });

    setWorkspaceMembers(current => current.filter(member => member.email.toLowerCase() !== email.toLowerCase()));
  };

  const handleSetWorkspaceMemberPassword = async (email: string, newPassword: string, resetPasswordOnLogin = false) => {
    const response = await fetch(`/api/team-members/${encodeURIComponent(email)}/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ newPassword, resetPasswordOnLogin }),
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }
  };

  const handleUpdateOwnPassword = async (currentPassword: string, newPassword: string) => {
    const response = await fetch("/api/profile/password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      }),
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }
  };

  const handleCreateRolePreset = async (input: Pick<RolePreset, "role" | "detail" | "capabilities">) => {
    await requestJson<RolePreset[]>("/api/roles", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  };

  const handleUpdateRolePreset = async (role: string, input: Pick<RolePreset, "role" | "detail" | "capabilities">) => {
    await requestJson<RolePreset[]>(`/api/roles/${encodeURIComponent(role)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  };

  const handleDeleteRolePreset = async (role: string) => {
    await requestJson<RolePreset[]>(`/api/roles/${encodeURIComponent(role)}`, {
      method: "DELETE",
    });
  };

  const applyWorkspaceTeams = (teams: UserTeam[]) => {
    setWorkspaceTeams(teams);
    setWorkspaceMembers(current => applyTeamsToWorkspaceMembers(current, teams));
  };

  const handleCreateWorkspaceTeam = async (input: Pick<UserTeam, "name" | "description" | "categoryIds">) => {
    const teams = await requestJson<UserTeam[]>("/api/teams", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });

    applyWorkspaceTeams(teams);
  };

  const handleUpdateWorkspaceTeam = async (teamId: string, input: Pick<UserTeam, "name" | "description" | "categoryIds">) => {
    const teams = await requestJson<UserTeam[]>(`/api/teams/${encodeURIComponent(teamId)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });

    applyWorkspaceTeams(teams);
  };

  const handleDeleteWorkspaceTeam = async (teamId: string) => {
    const teams = await requestJson<UserTeam[]>(`/api/teams/${encodeURIComponent(teamId)}`, {
      method: "DELETE",
    });

    applyWorkspaceTeams(teams);
  };

  const handleExportAccessLogs = async (range: { startDate: string; endDate: string; format?: 'csv' | 'json' }) => {
    const searchParams = new URLSearchParams();

    if (range.startDate) {
      searchParams.set('startDate', range.startDate);
    }

    if (range.endDate) {
      searchParams.set('endDate', range.endDate);
    }

    searchParams.set('format', range.format ?? 'csv');

    const response = await fetch(`/api/access-logs/export?${searchParams.toString()}`);

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('content-disposition') ?? '';
    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] ?? `access-logs.${range.format === 'json' ? 'json' : 'csv'}`;
    const anchor = document.createElement('a');

    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  const handleLoadAccessLogs = useCallback(async (range?: { startDate?: string; endDate?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();

    if (range?.startDate) {
      searchParams.set("startDate", range.startDate);
    }

    if (range?.endDate) {
      searchParams.set("endDate", range.endDate);
    }

    if (typeof range?.limit === "number") {
      searchParams.set("limit", String(range.limit));
    }

    return requestJson<AccessLog[]>(`/api/access-logs${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
  }, []);

  const handleLoadAuditEvents = useCallback(async (range?: { startDate?: string; endDate?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();

    if (range?.startDate) {
      searchParams.set("startDate", range.startDate);
    }

    if (range?.endDate) {
      searchParams.set("endDate", range.endDate);
    }

    if (typeof range?.limit === "number") {
      searchParams.set("limit", String(range.limit));
    }

    return requestJson<AuditEvent[]>(`/api/audit-events${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
  }, []);

  const handleLoadArchiveItems = useCallback(async (filters?: { query?: string; itemKind?: string; archiveKind?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();

    if (filters?.query) {
      searchParams.set("query", filters.query);
    }

    if (filters?.itemKind) {
      searchParams.set("itemKind", filters.itemKind);
    }

    if (filters?.archiveKind) {
      searchParams.set("archiveKind", filters.archiveKind);
    }

    if (typeof filters?.limit === "number") {
      searchParams.set("limit", String(filters.limit));
    }

    return requestJson<ArchiveItemSummary[]>(`/api/archive/items${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
  }, []);

  const handleLoadArchiveItemDetail = useCallback((itemId: string) => {
    return requestJson<ArchiveItemDetail>(`/api/archive/items/${encodeURIComponent(itemId)}`);
  }, []);

  const handleDeleteArchiveItem = useCallback((itemId: string) => {
    return requestJson<{ ok: boolean; deletedItemId: string; deletedArchiveId: string; deletedArchive: boolean; hasRemainingArchiveData: boolean }>(`/api/archive/items/${encodeURIComponent(itemId)}`, {
      method: "DELETE",
    }).then(result => {
      setHasArchiveData(result.hasRemainingArchiveData);
      return result;
    });
  }, []);

  const handleExportAuditEvents = async (range: { startDate: string; endDate: string; format?: 'csv' | 'json' }) => {
    const searchParams = new URLSearchParams();

    if (range.startDate) {
      searchParams.set('startDate', range.startDate);
    }

    if (range.endDate) {
      searchParams.set('endDate', range.endDate);
    }

    searchParams.set('format', range.format ?? 'csv');

    const response = await fetch(`/api/audit-events/export?${searchParams.toString()}`);

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('content-disposition') ?? '';
    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] ?? `audit-events.${range.format === 'json' ? 'json' : 'csv'}`;
    const anchor = document.createElement('a');

    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  const openTicketDetail = (ticketId: string) => {
    if (!canAccessTicketView) {
      return;
    }

    setActiveView("Tickets");
    setSelectedTicketId(ticketId);
    setSelectedTicketDetailId(ticketId);
    setSelectedCustomerProfileId(null);
    setIsQuickViewOpen(false);
    setIsCreatingArticle(false);
    setSelectedKnowledgeCollectionId(null);
    setSelectedKnowledgeArticleId(null);
    setIsEditingKnowledgeArticle(false);

    if (typeof window !== "undefined") {
      const nextPath = `${viewPaths.Tickets}/${encodeURIComponent(ticketId)}`;

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const handleLoadCalendarScheduledTickets = useCallback(async () => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const response = await fetch(buildBootstrapRequestPath(["shell", "customers", "tickets"]), { cache: "no-store" });
      const data = await parseJsonResponse<BootstrapPayload>(response);
      applyBootstrapPayload(data);
    } catch (error) {
      console.warn("Failed to load scheduled tickets for the calendar.", error);
    }
  }, [applyBootstrapPayload]);

  const openCustomerProfile = (customerId: string) => {
    if (!canAccessCustomerView) {
      return;
    }

    setActiveView("Customers");
    setSelectedTicketDetailId(null);
    setSelectedCustomerProfileId(customerId);
    setIsQuickViewOpen(false);
    setIsCreatingArticle(false);
    setSelectedKnowledgeCollectionId(null);
    setSelectedKnowledgeArticleId(null);
    setIsEditingKnowledgeArticle(false);

    if (typeof window !== "undefined") {
      const nextPath = `${viewPaths.Customers}/${encodeURIComponent(customerId)}`;

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const openKnowledgeArticle = (articleId: string, collectionId?: string | null) => {
    setActiveView("Knowledge Base");
    setIsQuickViewOpen(false);
    setSelectedTicketDetailId(null);
    setSelectedCustomerProfileId(null);
    setIsCreatingArticle(false);
    setIsEditingKnowledgeArticle(false);
    setSelectedKnowledgeCollectionId(collectionId ?? null);
    setSelectedKnowledgeArticleId(articleId);

    if (typeof window !== "undefined") {
      const nextPath = getKnowledgeBaseArticlePath(articleId);

      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
    }
  };

  const handleOpenGlobalSearchResult = (result: GlobalSearchResult) => {
    setGlobalSearchQuery("");
    setGlobalSearchFilter("all");
    setGlobalSearchCategory("all");

    if (result.kind === "ticket") {
      openTicketDetail(result.entityId);
      return;
    }

    if (result.kind === "customer") {
      openCustomerProfile(result.entityId);
      return;
    }

    openKnowledgeArticle(result.entityId, result.collectionId);
  };

  const handleCreateProductPlan = (productPlan: ProductPlan) => {
    setProductPlanItems(currentPlans => [productPlan, ...currentPlans]);

    void (async () => {
      try {
        const savedPlan = await parseJsonResponse<ProductPlan>(
          await fetch("/api/items", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(productPlan),
          }),
        );

        setProductPlanItems(currentPlans => [savedPlan, ...currentPlans.filter(plan => plan.id !== savedPlan.id)]);
      } catch (error) {
        console.warn("Failed to create product plan via API.", error);
      }
    })();
  };

  const handleDeleteProductPlan = (productPlan: ProductPlan) => {
    const linkedCustomer = customerItems.find(customer => customer.products.some(product => product.productPlanId === productPlan.id));

    if (linkedCustomer) {
      window.alert(`${productPlan.name} is still assigned to ${linkedCustomer.company}. Remove it from customer accounts before deleting the item.`);
      return;
    }

    const confirmed = window.confirm(`Delete ${productPlan.name}? This removes the item from settings and any linked bundle relationships.`);

    if (!confirmed) {
      return;
    }

    const previousPlans = productPlanItems;
    const previousWorkspaceSettings = workspaceSettings;

    setProductPlanItems(currentPlans =>
      currentPlans
        .filter(plan => plan.id !== productPlan.id)
        .map(plan => ({
          ...plan,
          relatedItemIds: plan.relatedItemIds.filter(relatedItemId => relatedItemId !== productPlan.id),
        })),
    );
    setWorkspaceSettings(currentSettings => ({
      ...currentSettings,
      caseEmailRoutes: currentSettings.caseEmailRoutes.map(route => (route.itemId === productPlan.id ? { ...route, itemId: null } : route)),
    }));

    void (async () => {
      try {
        const response = await fetch(`/api/items/${encodeURIComponent(productPlan.id)}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setProductPlanItems(previousPlans);
        setWorkspaceSettings(previousWorkspaceSettings);
        console.warn("Failed to delete product plan via API.", error);
      }
    })();
  };

  const handleUpdateProductPlan = (productPlan: ProductPlan) => {
    setProductPlanItems(currentPlans => currentPlans.map(plan => (plan.id === productPlan.id ? productPlan : plan)));

    void (async () => {
      try {
        const savedPlan = await parseJsonResponse<ProductPlan>(
          await fetch(`/api/items/${encodeURIComponent(productPlan.id)}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(productPlan),
          }),
        );

        setProductPlanItems(currentPlans => currentPlans.map(plan => (plan.id === savedPlan.id ? savedPlan : plan)));
      } catch (error) {
        console.warn("Failed to update product plan via API.", error);
      }
    })();
  };

  const renderView = () => {
    switch (activeView) {
      case "Dashboard":
        return (
          <DashboardView
            assignments={calendarAssignments}
            currentUserEmail={currentUserEmail}
            currentUserName={currentUserName}
            filteredTickets={filteredTickets}
            isLoading={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            language={appLanguage}
            onOpenTicket={openTicketDetail}
            timezone={profileTimezone}
          />
        );
      case "Calendar":
        return (
          <CalendarView
            currentUserEmail={currentUserEmail}
            currentUserRole={effectiveCurrentRole}
            canCreateAssignments={canCreateCalendarAssignmentsView}
            canManageAssignmentTypes={canManageCalendarAssignmentTypesView}
            isLoading={isBootstrapLoading}
            language={appLanguage}
            timezone={profileTimezone}
            teamMembers={workspaceMembers}
            teams={workspaceTeams}
            assignmentTypes={calendarAssignmentTypeOptions}
            assignments={calendarAssignments}
            tickets={ticketItems}
            customers={customerItems}
            onOpenTicket={openTicketDetail}
            onLoadScheduledTickets={handleLoadCalendarScheduledTickets}
            onLoadAssignmentTypeDefinitions={() => requestJson<CalendarAssignmentTypeDefinition[]>("/api/calendar/assignment-types/manage")}
            onCreateAssignmentType={async (teamId: string, type: string) => {
              const savedTypes = await requestJson<CalendarAssignmentTypeDefinition[]>("/api/calendar/assignment-types", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ teamId, name: type }),
              });
              setCalendarAssignmentTypeOptions(mergeCalendarAssignmentTypeOptions(savedTypes, calendarAssignments));
              return savedTypes;
            }}
            onUpdateAssignmentType={async (assignmentType: CalendarAssignmentTypeDefinition, nextName: string) => {
              const savedTypes = await requestJson<CalendarAssignmentTypeDefinition[]>(`/api/calendar/assignment-types/${encodeURIComponent(assignmentType.id)}`, {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ name: nextName }),
              });
              const nextAssignments = calendarAssignments.map(assignment => (
                assignment.teamId === assignmentType.teamId && assignment.type.toLowerCase() === assignmentType.name.toLowerCase()
                  ? { ...assignment, type: nextName }
                  : assignment
              ));
              setCalendarAssignments(nextAssignments);
              setCalendarAssignmentTypeOptions(mergeCalendarAssignmentTypeOptions(savedTypes, nextAssignments));
              return savedTypes;
            }}
            onDeleteAssignmentType={async (assignmentTypeId: string) => {
              const savedTypes = await requestJson<CalendarAssignmentTypeDefinition[]>(`/api/calendar/assignment-types/${encodeURIComponent(assignmentTypeId)}`, {
                method: "DELETE",
              });
              setCalendarAssignmentTypeOptions(mergeCalendarAssignmentTypeOptions(savedTypes, calendarAssignments));
              return savedTypes;
            }}
            onCreateAssignment={async (input: CalendarAssignmentInput) => {
              const savedAssignment = await requestJson<CalendarAssignment>("/api/calendar/assignments", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(input),
              });
              setCalendarAssignmentTypeOptions(currentTypes => mergeCalendarAssignmentTypes([...currentTypes, savedAssignment.type]));
              setCalendarAssignments(currentAssignments => sortCalendarAssignments([
                ...currentAssignments.filter(assignment => assignment.id !== savedAssignment.id),
                savedAssignment,
              ]));
            }}
            onUpdateAssignment={async (assignmentId: string, input: CalendarAssignmentInput) => {
              const savedAssignment = await requestJson<CalendarAssignment>(`/api/calendar/assignments/${encodeURIComponent(assignmentId)}`, {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(input),
              });
              setCalendarAssignmentTypeOptions(currentTypes => mergeCalendarAssignmentTypes([...currentTypes, savedAssignment.type]));
              setCalendarAssignments(currentAssignments => sortCalendarAssignments(currentAssignments.map(assignment => (
                assignment.id === assignmentId ? savedAssignment : assignment
              ))));
            }}
            onDeleteAssignment={async (assignmentId: string) => {
              const response = await fetch(`/api/calendar/assignments/${encodeURIComponent(assignmentId)}`, {
                method: "DELETE",
              });

              if (!response.ok) {
                throw new Error(await getErrorMessage(response));
              }

              setCalendarAssignments(currentAssignments => currentAssignments.filter(assignment => assignment.id !== assignmentId));
            }}
          />
        );
      case "Tickets":
        return selectedTicketDetail && selectedTicketDetailCustomer ? (
          <TicketDetailView
            language={appLanguage}
            ticket={selectedTicketDetail}
            customer={selectedTicketDetailCustomer}
            customers={customerItems}
            currentUser={currentUserName}
            timezone={profileTimezone}
            isLoadingAttachments={loadingTicketAttachmentIds.includes(selectedTicketDetail.id)}
            isSyncingTicket={syncingTicketIds.includes(selectedTicketDetail.id)}
            activities={selectedTicketActivities}
            relatedTickets={relatedTickets}
            knowledgeArticles={visibleKnowledgeBase.articles}
            productPlans={productPlanItems}
            ticketCategories={visibleTicketCategories}
            teamMembers={workspaceMembers}
            canEditTickets={canManageTickets}
            canDeleteTickets={canRemoveTickets}
            onAssignTicket={(id, assignee) => {
              handleTicketAssignmentChange(id, assignee);
            }}
            onDeleteTicket={handleDeleteTicket}
            onUpdateCategory={handleTicketCategoryChange}
            onUpdateDescription={(id, description) => {
              handleTicketMutation(id, { description });
            }}
            onUpdateLinkedArticles={(id, linkedArticleIds) => {
              handleTicketMutation(id, { linkedArticleIds });
            }}
            onUpdateCustomFieldValues={(id, customFieldValues) => {
              handleTicketMutation(id, { customFieldValues });
            }}
            onUpdateSchedule={handleTicketScheduleChange}
            onUpdateNotes={(id, updates) => {
              handleTicketMutation(id, updates);
            }}
            onUpdateCustomer={handleTicketCustomerChange}
            onUpdateProduct={handleTicketProductChange}
            onUpdatePriority={handleTicketPriorityChange}
            onUpdateStatus={handleTicketStatusChange}
            onUploadAttachments={handleTicketAttachmentUpload}
            onDeleteAttachment={handleTicketAttachmentDelete}
            attachmentOverridesEnabled={workspaceSettings.attachmentDownloadOverridesEnabled}
            attachmentPublicDefaultEnabled={workspaceSettings.publicAttachmentDownloadsEnabled}
            onSetAttachmentShare={handleTicketAttachmentShareChange}
            onOpenArticle={openKnowledgeArticle}
            onOpenCustomer={customerId => {
              setActiveView("Customers");
              setSelectedTicketDetailId(null);
              setSelectedCustomerProfileId(customerId);

              if (typeof window !== "undefined") {
                const nextPath = `${viewPaths.Customers}/${encodeURIComponent(customerId)}`;
                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
            onOpenTicket={openTicketDetail}
            onBack={() => {
              setSelectedTicketDetailId(null);

              if (typeof window !== "undefined" && window.location.pathname !== viewPaths.Tickets) {
                window.history.pushState({}, "", viewPaths.Tickets);
              }
            }}
          />
        ) : (
          <TicketsView
            language={appLanguage}
            customers={customerItems}
            productPlans={productPlanItems}
            ticketCategories={visibleTicketCategories}
            teamMembers={workspaceMembers}
            currentUser={currentUserName}
            timezone={profileTimezone}
            canEditTickets={canManageTickets}
            isLoadingTickets={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            filteredTickets={filteredTickets}
            syncingTicketIds={syncingTicketIds}
            selectedTicket={selectedTicket}
            selectedCustomer={selectedCustomer}
            isQuickViewOpen={isQuickViewOpen}
            counts={counts}
            onSelectTicket={setSelectedTicketId}
            onCreateTicket={handleCreateTicket}
            onAssignTicket={(id, assignee) => {
              handleTicketAssignmentChange(id, assignee);
            }}
            onUpdateCategory={handleTicketCategoryChange}
            onUpdateProduct={handleTicketProductChange}
            onUpdatePriority={handleTicketPriorityChange}
            onUpdateStatus={handleTicketStatusChange}
            onOpenTicket={openTicketDetail}
            onOpenQuickView={id => {
              setSelectedTicketId(id);
              setIsQuickViewOpen(true);
            }}
            onCloseQuickView={() => setIsQuickViewOpen(false)}
          />
        );
      case "Customers":
        return selectedCustomerProfile ? (
          <CustomerDetailView
            customer={selectedCustomerProfile}
            productPlans={productPlanItems}
            tickets={selectedCustomerTickets}
            expenses={expenseItems}
            expenseVendors={expenseVendorItems}
            currency={workspaceSettings.currency}
            teamMembers={workspaceMembers}
            timezone={profileTimezone}
            language={appLanguage}
            currentUser={currentUserName}
            canEditCustomers={canManageCustomerAccounts}
            canDeleteCustomers={canRemoveCustomerAccounts}
            onOpenTicket={openTicketDetail}
            onUpdateCustomer={handleCustomerChange}
            onDeleteCustomer={handleDeleteCustomer}
            onBack={() => {
              setSelectedCustomerProfileId(null);

              if (typeof window !== "undefined" && window.location.pathname !== viewPaths.Customers) {
                window.history.pushState({}, "", viewPaths.Customers);
              }
            }}
          />
        ) : (
          <CustomersView
            customers={customerItems}
            tickets={visibleTicketItems}
            language={appLanguage}
            canEditCustomers={canManageCustomerAccounts}
            isLoadingCustomers={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            onCreateCustomer={handleCreateCustomer}
            onDeleteCustomer={handleDeleteCustomer}
            onOpenCustomer={customerId => {
              openCustomerProfile(customerId);
            }}
          />
        );
      case "Users":
        return (
          <UsersView
            language={appLanguage}
            currentUserEmail={currentUserEmail}
            currentUserRole={effectiveCurrentRole}
            billingPlan={workspaceSettings.billingPlan}
            canEditUsers={canEditUsersView}
            isLoadingUsers={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            ticketCategories={ticketCategoryItems}
            teamMembers={workspaceMembers}
            teams={workspaceTeams}
            onLoadRolePresets={loadRolePresets}
            onCreateRolePreset={handleCreateRolePreset}
            onDeleteRolePreset={handleDeleteRolePreset}
            onCreateUser={handleCreateWorkspaceMember}
            onCreateTeam={handleCreateWorkspaceTeam}
            onSetUserPassword={handleSetWorkspaceMemberPassword}
            onUpdateRolePreset={handleUpdateRolePreset}
            onUpdateTeam={handleUpdateWorkspaceTeam}
            onDeleteUser={handleDeleteWorkspaceMember}
            onDeleteTeam={handleDeleteWorkspaceTeam}
            onUpdateUser={handleUpdateWorkspaceMember}
          />
        );
      case "Logs":
        return (
          <LogsView
            language={appLanguage}
            workspaceSettings={workspaceSettings}
            onLoadAccessLogs={handleLoadAccessLogs}
            onLoadAuditEvents={handleLoadAuditEvents}
            onExportAccessLogs={handleExportAccessLogs}
            onExportAuditEvents={handleExportAuditEvents}
          />
        );
      case "Archive":
        return (
          <ArchiveView
            language={appLanguage}
            canEditArchive={canEditArchiveView}
            onLoadArchiveItems={handleLoadArchiveItems}
            onLoadArchiveItemDetail={handleLoadArchiveItemDetail}
            onDeleteArchiveItem={handleDeleteArchiveItem}
          />
        );
      case "Knowledge Base":
        return isCreatingArticle && canCreateKbArticles ? (
          <NewArticleView
            collections={visibleKnowledgeBase.collections}
            ticketCategories={visibleTicketCategories}
            teamMembers={workspaceMembers}
            currentUserEmail={currentKnowledgeUserEmail}
            language={appLanguage}
            article={isEditingKnowledgeArticle ? selectedKnowledgeArticle ?? undefined : undefined}
            defaultCollectionId={selectedKnowledgeCollectionId}
            onSaveArticle={handleSaveKnowledgeArticle}
            onSaveComplete={savedArticle => {
              setIsCreatingArticle(false);
              setIsEditingKnowledgeArticle(false);
              setSelectedKnowledgeArticleId(savedArticle.id);
              setSelectedKnowledgeCollectionId(savedArticle.collectionId ?? null);

              if (typeof window !== "undefined") {
                const nextPath = getKnowledgeBaseArticlePath(savedArticle.id);

                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
            onBack={() => {
              setIsCreatingArticle(false);
              setIsEditingKnowledgeArticle(false);

              if (typeof window !== "undefined") {
                const nextPath = selectedKnowledgeArticleId && !isEditingKnowledgeArticle
                  ? getKnowledgeBaseArticlePath(selectedKnowledgeArticleId)
                  : selectedKnowledgeArticleId && isEditingKnowledgeArticle
                    ? getKnowledgeBaseArticlePath(selectedKnowledgeArticleId)
                    : selectedKnowledgeCollectionId
                      ? getKnowledgeBaseCollectionPath(selectedKnowledgeCollectionId)
                      : viewPaths["Knowledge Base"];

                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
          />
        ) : selectedKnowledgeArticle ? (
          <KnowledgeArticleView
            article={selectedKnowledgeArticle}
            collection={selectedKnowledgeArticleCollection}
            language={appLanguage}
            timezone={profileTimezone}
            canManageArticle={canManageSelectedKnowledgeArticle}
            teamMembers={workspaceMembers}
            currentUserEmail={currentKnowledgeUserEmail}
            onDelete={() => handleDeleteKnowledgeArticle(selectedKnowledgeArticle.id, selectedKnowledgeCollectionId ?? selectedKnowledgeArticle.collectionId)}
            onToggleFeatured={() => handleToggleKnowledgeArticleFeatured(selectedKnowledgeArticle)}
            onBack={() => {
              setSelectedKnowledgeArticleId(null);
              setIsEditingKnowledgeArticle(false);

              if (typeof window !== "undefined") {
                const nextPath = selectedKnowledgeCollectionId
                  ? getKnowledgeBaseCollectionPath(selectedKnowledgeCollectionId)
                  : selectedKnowledgeArticle.collectionId
                    ? getKnowledgeBaseCollectionPath(selectedKnowledgeArticle.collectionId)
                    : viewPaths["Knowledge Base"];

                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
            onEdit={() => {
              setIsCreatingArticle(true);
              setIsEditingKnowledgeArticle(true);

              if (typeof window !== "undefined") {
                const nextPath = getKnowledgeBaseArticleEditPath(selectedKnowledgeArticle.id);

                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
          />
        ) : selectedKnowledgeCollection ? (
          <KnowledgeCollectionView
            collection={selectedKnowledgeCollection}
            articles={selectedKnowledgeCollectionArticles}
            language={appLanguage}
            canCreateArticle={canCreateKbArticles}
            canManageCollection={canManageKbContent}
            onDeleteCollection={() => handleDeleteKnowledgeCollection(selectedKnowledgeCollection.id)}
            onOpenArticle={articleId => {
              setIsEditingKnowledgeArticle(false);
              openKnowledgeArticle(articleId, selectedKnowledgeCollection.id);
            }}
            onBack={() => {
              setSelectedKnowledgeCollectionId(null);

              if (typeof window !== "undefined" && window.location.pathname !== viewPaths["Knowledge Base"]) {
                window.history.pushState({}, "", viewPaths["Knowledge Base"]);
              }
            }}
            onCreateArticle={() => {
              if (!canCreateKbArticles) {
                return;
              }

              setActiveView("Knowledge Base");
              setIsCreatingArticle(true);
              setSelectedKnowledgeArticleId(null);
              setIsEditingKnowledgeArticle(false);

              if (typeof window !== "undefined" && window.location.pathname !== knowledgeBaseNewArticlePath) {
                window.history.pushState({}, "", knowledgeBaseNewArticlePath);
              }
            }}
          />
        ) : (
          <KnowledgeBaseView
            knowledgeBase={visibleKnowledgeBase}
            teamMembers={workspaceMembers}
            currentUserEmail={currentKnowledgeUserEmail}
            language={appLanguage}
            isLoadingKnowledgeBase={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            canCreateArticle={canCreateKbArticles}
            canCreateCollection={canManageKbContent}
            onCreateCollection={handleCreateKnowledgeCollection}
            onOpenArticle={articleId => {
              setSelectedKnowledgeCollectionId(null);
              openKnowledgeArticle(articleId, null);
            }}
            onCreateArticle={() => {
              if (!canCreateKbArticles) {
                return;
              }

              setActiveView("Knowledge Base");
              setIsCreatingArticle(true);
              setSelectedKnowledgeArticleId(null);
              setIsEditingKnowledgeArticle(false);

              if (typeof window !== "undefined" && window.location.pathname !== knowledgeBaseNewArticlePath) {
                window.history.pushState({}, "", knowledgeBaseNewArticlePath);
              }
            }}
            onOpenCollection={collectionId => {
              setActiveView("Knowledge Base");
              setIsCreatingArticle(false);
              setSelectedKnowledgeArticleId(null);
              setIsEditingKnowledgeArticle(false);
              setSelectedKnowledgeCollectionId(collectionId);

              if (typeof window !== "undefined") {
                const nextPath = getKnowledgeBaseCollectionPath(collectionId);

                if (window.location.pathname !== nextPath) {
                  window.history.pushState({}, "", nextPath);
                }
              }
            }}
          />
        );
      case "Notifications":
        return (
          <NotificationsView
            notifications={tenantNotifications}
            language={appLanguage}
            isLoading={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            onDismissNotification={handleDismissTenantNotification}
          />
        );
      case "Reporting":
        return (
          <SimplifiedReportingView
            customers={customerItems}
            currency={workspaceSettings.currency}
            expenses={expenseItems}
            vendors={expenseVendorItems}
            isLoadingReporting={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            language={appLanguage}
            productPlans={productPlanItems}
            tickets={ticketItems}
            canViewAllExpenses={canViewAllExpensesView}
            members={workspaceMembers}
          />
        );
      case "Items":
        return (
          <ItemsView
            branding={appBranding}
            workspaceSettings={workspaceSettings}
            productPlans={productPlanItems}
            language={appLanguage}
            canManageItems={canManageItemsView}
            isLoadingItems={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            onCreateProductPlan={handleCreateProductPlan}
            onDeleteProductPlan={handleDeleteProductPlan}
            onUpdateProductPlan={handleUpdateProductPlan}
          />
        );
      case "Vendors":
        return (
          <VendorsView
            vendors={expenseVendorItems}
            expenses={expenseItems}
            currency={workspaceSettings.currency}
            language={appLanguage}
            isLoading={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            onCreateVendor={handleCreateVendor}
            onUpdateVendor={handleUpdateVendor}
            onDeleteVendor={handleDeleteVendor}
          />
        );
      case "Expenses":
        return (
          <ExpenseTrackerView
            vendors={expenseVendorItems}
            expenses={expenseItems}
            customers={customerItems}
            currency={workspaceSettings.currency}
            language={appLanguage}
            isLoading={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            onCreateExpense={handleCreateExpense}
            onUpdateExpense={handleUpdateExpense}
            onDeleteExpense={handleDeleteExpense}
            canManageInvoices={canManageExpensesView}
            canEditExpenses={canManageExpensesView}
            canDeleteExpenses={canDeleteExpensesView}
            canEditAllExpenses={canEditAllExpensesView}
            currentUserEmail={currentKnowledgeUserEmail}
            onDeleteInvoice={handleDeleteExpenseInvoice}
            canViewAllExpenses={canViewAllExpensesView}
            canCreateVendors={canManageVendorsView}
            onCreateVendor={handleCreateVendor}
            members={workspaceMembers}
          />
        );
      case "Settings":
        return (
          <SettingsView
            branding={appBranding}
            colorMode={colorMode}
            tenantEmailDomain={currentTenant?.hostname ?? tenantHostnameCandidate}
            language={appLanguage}
            workspaceSettings={workspaceSettings}
            isLoadingSettings={isBootstrapLoading || !hasLoadedBootstrap || isCurrentViewBootstrapPending}
            emailRoutingSyncState={emailRoutingSyncState}
            emailRoutingSyncMessage={emailRoutingSyncMessage}
            canManageAdminSettings={canManageAdminSettingsView}
            canManageBilling={canManageBillingView}
            canManageItems={canManageItemsView}
            productPlans={productPlanItems}
            ticketCategories={ticketCategoryItems}
            knowledgeArticles={knowledgeBase.articles}
            tickets={ticketItems}
            billingUsage={billingUsage ?? undefined}
            section={settingsSection}
            onNavigateToSection={handleNavigateToSettingsSection}
            onBackToSettingsOverview={handleBackToSettingsOverview}
            onCreateProductPlan={handleCreateProductPlan}
            onDeleteProductPlan={handleDeleteProductPlan}
            onLogoUpload={handleLogoUpload}
            onPrimaryColorChange={handlePrimaryColorChange}
            onConsoleNameChange={handleConsoleNameChange}
            onRemoveLogo={handleRemoveLogo}
            onRefreshCaseEmailRouteOutboundStatuses={handleRefreshCaseEmailRouteOutboundStatuses}
            onLoadRestorePoints={handleLoadTenantRestorePoints}
            onRestoreDatabase={handleRestoreTenantDatabase}
            onToggleCaseEmailRouteOutbound={handleToggleCaseEmailRouteOutbound}
            onUpdateWorkspaceSettings={handleWorkspaceSettingsChange}
            onRefreshWorkspaceSettings={handleRefreshWorkspaceSettings}
            onCreateTicketCategory={ticketCategory => {
              setTicketCategoryItems(currentCategories => [normalizeTicketCategoryRecord(ticketCategory), ...currentCategories]);

              void (async () => {
                try {
                  const savedCategory = await parseJsonResponse<TicketCategory>(
                    await fetch("/api/categories", {
                      method: "POST",
                      headers: { "content-type": "application/json" },
                      body: JSON.stringify(ticketCategory),
                    }),
                  );

                  const normalizedCategory = normalizeTicketCategoryRecord(savedCategory);
                  setTicketCategoryItems(currentCategories => [normalizedCategory, ...currentCategories.filter(category => category.id !== normalizedCategory.id)]);
                } catch (error) {
                  console.warn("Failed to create ticket category via API.", error);
                }
              })();
            }}
            onDeleteTicketCategory={ticketCategory => {
              const linkedTicket = ticketItems.find(ticket => ticket.category === ticketCategory.name);
              const linkedArticle = knowledgeBase.articles.find(article => article.audience === ticketCategory.name);

              if (linkedTicket) {
                window.alert(`${ticketCategory.name} is still used by ${linkedTicket.id}. Reassign those tickets before deleting the category.`);
                return;
              }

              if (linkedArticle) {
                window.alert(`${ticketCategory.name} is still used by ${linkedArticle.title}. Reassign that knowledge article before deleting the category.`);
                return;
              }

              const confirmed = window.confirm(`Delete ${ticketCategory.name}? This removes the category from settings.`);

              if (!confirmed) {
                return;
              }

              const previousCategories = ticketCategoryItems;
              const previousWorkspaceSettings = workspaceSettings;

              setTicketCategoryItems(currentCategories => currentCategories.filter(category => category.id !== ticketCategory.id));
              setWorkspaceSettings(currentSettings => ({
                ...currentSettings,
                caseEmailRoutes: currentSettings.caseEmailRoutes.map(route => (route.categoryId === ticketCategory.id ? { ...route, categoryId: null } : route)),
              }));

              void (async () => {
                try {
                  const response = await fetch(`/api/categories/${encodeURIComponent(ticketCategory.id)}`, {
                    method: "DELETE",
                  });

                  if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                  }
                } catch (error) {
                  setTicketCategoryItems(previousCategories);
                  setWorkspaceSettings(previousWorkspaceSettings);
                  console.warn("Failed to delete ticket category via API.", error);
                }
              })();
            }}
            onUpdateTicketCategory={(ticketCategory, previousName) => {
              const normalizedCategory = normalizeTicketCategoryRecord(ticketCategory);
              setTicketCategoryItems(currentCategories => currentCategories.map(category => (category.id === normalizedCategory.id ? normalizedCategory : category)));

              if (previousName !== normalizedCategory.name) {
                setTicketItems(currentTickets => currentTickets.map(ticket => (ticket.category === previousName ? { ...ticket, category: normalizedCategory.name } : ticket)));
                setKnowledgeBase(currentKnowledgeBase => ({
                  ...currentKnowledgeBase,
                  featuredArticles: currentKnowledgeBase.featuredArticles.map(article => (article.audience === previousName ? { ...article, audience: normalizedCategory.name } : article)),
                  popularArticles: currentKnowledgeBase.popularArticles.map(article => (article.audience === previousName ? { ...article, audience: normalizedCategory.name } : article)),
                  articles: currentKnowledgeBase.articles.map(article => (article.audience === previousName ? { ...article, audience: normalizedCategory.name } : article)),
                }));
              }

              void (async () => {
                try {
                  const savedCategory = await parseJsonResponse<TicketCategory>(
                    await fetch(`/api/categories/${encodeURIComponent(ticketCategory.id)}`, {
                      method: "PUT",
                      headers: { "content-type": "application/json" },
                      body: JSON.stringify(ticketCategory),
                    }),
                  );

                  const normalizedSavedCategory = normalizeTicketCategoryRecord(savedCategory);
                  setTicketCategoryItems(currentCategories => currentCategories.map(category => (category.id === normalizedSavedCategory.id ? normalizedSavedCategory : category)));

                  if (previousName !== normalizedSavedCategory.name) {
                    setTicketItems(currentTickets => currentTickets.map(ticket => (ticket.category === previousName ? { ...ticket, category: normalizedSavedCategory.name } : ticket)));
                    setKnowledgeBase(currentKnowledgeBase => ({
                      ...currentKnowledgeBase,
                      featuredArticles: currentKnowledgeBase.featuredArticles.map(article => (article.audience === previousName ? { ...article, audience: normalizedSavedCategory.name } : article)),
                      popularArticles: currentKnowledgeBase.popularArticles.map(article => (article.audience === previousName ? { ...article, audience: normalizedSavedCategory.name } : article)),
                      articles: currentKnowledgeBase.articles.map(article => (article.audience === previousName ? { ...article, audience: normalizedSavedCategory.name } : article)),
                    }));
                  }
                } catch (error) {
                  console.warn("Failed to update ticket category via API.", error);
                }
              })();
            }}
            onUpdateProductPlan={handleUpdateProductPlan}
          />
        );
    }
  };

  const handleLogin = async () => {
    if (!loginEmail.trim() || !loginPassword) {
      setAuthError("Enter your email and password.");
      return;
    }

    setIsSubmittingLogin(true);
    setAuthError(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: loginEmail.trim(),
          password: loginPassword,
        }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? `Request failed with status ${response.status}`);
      }

      const data = await parseJsonResponse<AuthSessionPayload>(response);
      setAuthEnabled(data.enabled);
      setIsAuthenticated(!data.enabled || data.authenticated);
      setAuthChecked(true);
      setCurrentUserEmail(data.email ?? null);
      setCurrentUserRole(data.tenant?.role ?? null);
      setCurrentTenant(data.tenant ?? null);
      setCurrentUserName(data.name ?? "");
      setCurrentUserProfile(emptyUserProfile);
      setMustChangePassword(Boolean(data.mustChangePassword));
      setLoginPassword("");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Unable to sign in.");
    } finally {
      setIsSubmittingLogin(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Ignore logout transport errors and still clear local auth state.
    }

    setIsAuthenticated(false);
    setLoginPassword("");
    setCurrentUserEmail(null);
    setCurrentUserRole(null);
    setCurrentTenant(null);
    setCurrentUserProfile(emptyUserProfile);
    setMustChangePassword(false);
    setForcedCurrentPassword("");
    setForcedNewPassword("");
    setForcedConfirmPassword("");
    setForcedPasswordError(null);
  };

  const handleForcedPasswordUpdate = async () => {
    if (!forcedCurrentPassword || !forcedNewPassword || !forcedConfirmPassword) {
      setForcedPasswordError("Enter your temporary password, then choose and confirm a new password.");
      return;
    }

    if (forcedNewPassword.length < 8) {
      setForcedPasswordError("New passwords need at least 8 characters.");
      return;
    }

    if (forcedNewPassword !== forcedConfirmPassword) {
      setForcedPasswordError("The password confirmation does not match.");
      return;
    }

    setIsUpdatingForcedPassword(true);
    setForcedPasswordError(null);

    try {
      await handleUpdateOwnPassword(forcedCurrentPassword, forcedNewPassword);
      setMustChangePassword(false);
      setForcedCurrentPassword("");
      setForcedNewPassword("");
      setForcedConfirmPassword("");
    } catch (error) {
      setForcedPasswordError(error instanceof Error ? error.message : "Unable to update your password.");
    } finally {
      setIsUpdatingForcedPassword(false);
    }
  };

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f3f3f4] px-6 py-10">
        <div className="rounded-[28px] border border-white/70 bg-white px-8 py-6 text-center shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">Preparing workspace</p>
          <p className="mt-3 text-sm font-medium text-[#475467]">Checking access…</p>
        </div>
      </main>
    );
  }

  if (tenantNotFoundHost) {
    return (
      <TenantNotFoundView
        hostname={tenantNotFoundHost}
        landingUrl={`https://${tenantRootDomain}`}
        appUrl={`https://app.${tenantRootDomain}`}
      />
    );
  }

  if (authEnabled && (!isAuthenticated || mustChangePassword)) {
    const loginViewMode = mustChangePassword
      ? "force-reset"
      : tenantAuthRoute === "reset-password"
        ? resetToken
          ? "confirm-reset"
          : "request-reset"
        : "login";

    return (
      <LoginView
        language={appLanguage}
        mode={loginViewMode}
        passwordResetEnabled={localPasswordResetEnabled}
        email={loginEmail}
        password={loginPassword}
        forcedCurrentPassword={forcedCurrentPassword}
        forcedPasswordError={forcedPasswordError}
        isUpdatingForcedPassword={isUpdatingForcedPassword}
        newPassword={mustChangePassword ? forcedNewPassword : newPassword}
        confirmPassword={mustChangePassword ? forcedConfirmPassword : confirmPassword}
        isSubmitting={isSubmittingLogin}
        isRequestingPasswordReset={isRequestingPasswordReset}
        isSubmittingPasswordReset={isSubmittingPasswordReset}
        error={authError}
        resetRequestError={resetRequestError}
        resetRequestNotice={resetRequestNotice}
        resetSubmitError={resetSubmitError}
        resetSubmitNotice={resetSubmitNotice}
        onEmailChange={setLoginEmail}
        onPasswordChange={setLoginPassword}
        onForcedCurrentPasswordChange={setForcedCurrentPassword}
        onNewPasswordChange={mustChangePassword ? setForcedNewPassword : setNewPassword}
        onConfirmPasswordChange={mustChangePassword ? setForcedConfirmPassword : setConfirmPassword}
        onSubmit={handleLogin}
        onUpdateForcedPassword={handleForcedPasswordUpdate}
        onRequestPasswordReset={handleRequestPasswordReset}
        onResetPassword={handleResetPassword}
        onShowPasswordReset={() => navigateToTenantAuthRoute("reset-password")}
        onBackToSignIn={() => navigateToTenantAuthRoute("login")}
      />
    );
  }

  const workspaceDisplayName = workspaceSettings.workspaceName.trim() || "your workspace";
  const welcomeOwnerName = currentUserName.trim() || "there";
  const welcomeRoleLabel = effectiveCurrentRole || currentUserRole || "Owner";
  const welcomePlanLabel = workspaceSettings.billingPlan
    ? `${workspaceSettings.billingPlan.charAt(0).toUpperCase()}${workspaceSettings.billingPlan.slice(1)} plan`
    : "Growth plan";
  const isFreeWelcomePlan = workspaceSettings.billingPlan.trim().toLowerCase() === "free";
  const welcomeHighlights = [
    {
      label: "Workspace",
      value: workspaceDisplayName,
      detail: "Provisioned and ready for your team.",
    },
    {
      label: "Access",
      value: welcomeRoleLabel,
      detail: "Administrative access is active.",
    },
    {
      label: "Plan",
      value: welcomePlanLabel,
      detail: "Limits and billing can be refined later.",
    },
  ];
  const welcomeSetupSteps = [
    {
      title: "Define categories, roles, teams, and users",
      description: "Start in Settings by creating ticket categories, then establish roles, teams, and user access so work can be routed with clean ownership from the beginning.",
      detail: "Settings · Categories, Roles, Teams, Users",
    },
    {
      title: "Create your items",
      description: "Once your internal structure is in place, define the items your team supports so tickets, reporting, and customer workflows align to the right services.",
      detail: "Items · Products, Plans, Services",
    },
    {
      title: "Turn on ticket-generating email addresses",
      description: "After categories and items exist, connect the email addresses that should generate tickets so incoming work lands in the right operating structure.",
      detail: "Settings · Email Routing, Intake Addresses",
    },
  ];

  return (
    <>
    <AppShell
      activeView={activeView}
      availableViews={availableViews}
      language={appLanguage}
      colorMode={colorMode}
      branding={appBranding}
      currentUser={currentUserName}
      currentUserEmail={currentUserEmail}
      currentUserAvatarUrl={currentUserProfile.avatarUrl}
      currentUserRole={effectiveCurrentRole}
      apiAccessEnabled={workspaceSettings.apiAccessEnabled}
      defaultApiKeyExpiryDays={workspaceSettings.apiKeyExpiryDays}
      navMenuMode={profileNavMenuMode}
      canManageApiKeys={canManageOwnApiKeys}
      canGenerateApiKeys={canGenerateApiKeys}
      apiKeys={profileApiKeys}
      latestGeneratedApiKey={latestGeneratedApiKey}
      onNavigate={handleNavigate}
      onColorModeChange={setColorMode}
      onLanguageChange={setAppLanguage}
      onNavMenuModeChange={setProfileNavMenuMode}
      onOpenProfileSettings={canManageOwnApiKeys ? () => {
        void loadProfileApiKeys();
      } : undefined}
      onGenerateApiKey={handleGenerateApiKey}
      onDismissGeneratedApiKey={() => setLatestGeneratedApiKey(null)}
      onRevokeApiKey={handleRevokeApiKey}
      onAvatarUpload={authEnabled && isAuthenticated ? handleProfileAvatarUpload : undefined}
      onAvatarRemove={authEnabled && isAuthenticated ? handleProfileAvatarRemove : undefined}
      onUpdatePassword={authEnabled && isAuthenticated ? handleUpdateOwnPassword : undefined}
      onSignOut={isAuthenticated ? handleSignOut : undefined}
      search={globalSearchQuery}
      searchFilter={globalSearchFilter}
      searchCategory={globalSearchCategory}
      searchCategoryOptions={globalSearchCategoryOptions}
      searchResults={globalSearchResults}
      isSearchLoading={isGlobalSearchLoading}
      timezone={profileTimezone}
      onOpenSearchResult={handleOpenGlobalSearchResult}
      onSearchCategoryChange={setGlobalSearchCategory}
      onSearchFilterChange={setGlobalSearchFilter}
      onTimezoneChange={setProfileTimezone}
      onSearchChange={setGlobalSearchQuery}
    >
      {ticketUpdateError ? (
        <div className="mb-6 rounded-[20px] border border-[#fda4af] bg-[#fff1f2] px-5 py-4 text-sm text-[#9f1239] shadow-[0_12px_24px_rgba(190,24,93,0.08)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">Ticket update failed</p>
              <p className="mt-1 text-[#881337]">{ticketUpdateError}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-full border border-[#fecdd3] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#9f1239] transition hover:border-[#fb7185] hover:bg-white"
              onClick={() => setTicketUpdateError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
      {renderView()}

      {renderedBannerNotification ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[70] px-4 sm:px-6 lg:px-8">
          <div className={`notification-banner-card mx-auto max-w-4xl rounded-[24px] border border-[#d0d5dd] bg-white/95 p-3 shadow-[0_24px_70px_rgba(15,23,42,0.20)] backdrop-blur-xl ${isNotificationBannerVisible ? "notification-banner-card--visible pointer-events-auto" : "notification-banner-card--hidden pointer-events-none"}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" onClick={handleOpenNotifications} className="flex min-w-0 flex-1 items-start gap-3 rounded-[18px] px-2 py-1 text-left transition hover:bg-[#f8fafc]">
                <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${renderedBannerNotification.severity === "critical" ? "bg-[#dc2626]" : renderedBannerNotification.severity === "warning" ? "bg-[#f59e0b]" : renderedBannerNotification.severity === "success" ? "bg-[#16a34a]" : "bg-[#0ea5e9]"}`} aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold tracking-[-0.01em] text-[#101828]">{renderedBannerNotification.title}</span>
                  <span className="mt-0.5 block line-clamp-2 text-xs leading-5 text-[#475467]">{renderedBannerNotification.body}</span>
                </span>
              </button>
              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                <button type="button" onClick={handleOpenNotifications} className="rounded-full border border-[#d0d5dd] px-4 py-2 text-xs font-semibold text-[#344054] transition hover:border-[#98a2b3] hover:bg-[#f8fafc]">
                  View details
                </button>
                <button type="button" onClick={() => handleDismissTenantNotification(renderedBannerNotification.id)} className="rounded-full bg-[#101828] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#1d2939]">
                  Don’t show again
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AnimatedModal
        open={isWelcomeModalOpen}
        onClose={handleDismissWelcomeModal}
        backdropAriaLabel="Dismiss welcome"
        overlayClassName="fixed inset-0 z-[90] bg-[linear-gradient(180deg,rgba(15,23,42,0.68),rgba(15,23,42,0.52))] backdrop-blur-[10px]"
        overlayStyle={{}}
        dialogClassName="owner-welcome-modal-shell app-modal-dialog relative max-h-[calc(100vh-3rem)] w-full max-w-4xl overflow-y-auto rounded-[36px] border border-white/70 bg-[#f8fafc] p-3 shadow-[0_40px_140px_rgba(15,23,42,0.28)] custom-scrollbar"
      >
        <div className="owner-welcome-modal relative overflow-hidden rounded-[32px] border border-white/80 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
          <button
            type="button"
            onClick={handleDismissWelcomeModal}
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/70 bg-white/90 text-lg font-semibold text-slate-500 shadow-[0_14px_30px_rgba(15,23,42,0.12)] backdrop-blur transition-colors hover:bg-white hover:text-[#111827]"
          >
            ×
          </button>

          <div className="relative overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,rgba(110,231,183,0.32),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(56,189,248,0.20),transparent_32%),linear-gradient(135deg,#0f172a_0%,#101828_48%,#14532d_100%)] px-6 py-7 text-white sm:px-8 sm:py-8">
            <div className="absolute -left-10 top-10 h-32 w-32 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
            <div className="absolute bottom-0 right-0 h-40 w-40 translate-x-8 translate-y-8 rounded-full bg-emerald-300/20 blur-3xl" aria-hidden="true" />

            <div className="relative">
              <div className="max-w-3xl pr-12 sm:pr-16">
                <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/80">
                  Owner onboarding
                </div>
                <h3 className="mt-4 font-headline text-3xl font-extrabold tracking-[-0.05em] text-white sm:text-[2.4rem]">
                  Welcome to Kuii
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-white/78 sm:text-[15px]">
                  Your workspace is live and ready for setup — a few deliberate choices now will give your team a polished, scalable support operation from day one.
                </p>
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-3">
                {welcomeHighlights.map((item) => (
                  <div
                    key={item.label}
                    className="min-h-[128px] rounded-[24px] border border-white/12 bg-white/10 px-5 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-sm"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60">{item.label}</p>
                    <p className="mt-3 text-lg font-semibold leading-7 text-white">{item.value}</p>
                    <p className="mt-2 max-w-[18rem] text-sm leading-6 text-white/68">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="owner-welcome-modal-body px-6 py-6 sm:px-8 sm:py-8">
            <div className="flex flex-col gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">Recommended setup order</p>
              <h4 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-[#101828]">
                Build the operating structure first, then open intake.
              </h4>
            </div>

            <div className="mt-5 grid gap-3">
              {welcomeSetupSteps.map((item, index) => (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-[#e4e7ec] bg-white p-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)] transition hover:border-[#d0d5dd] hover:shadow-[0_18px_40px_rgba(15,23,42,0.08)]"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(180deg,#ecfdf3_0%,#d1fadf_100%)] text-sm font-bold text-[#067647] shadow-[inset_0_0_0_1px_rgba(6,118,71,0.08)]">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-lg font-semibold tracking-[-0.02em] text-[#101828]">{item.title}</p>
                      <p className="mt-1.5 text-sm leading-6 text-[#475467]">{item.description}</p>
                      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{item.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="owner-welcome-modal-defaults mt-4 rounded-[26px] border border-[#bfd6ff] bg-[linear-gradient(180deg,#f5f9ff_0%,#e8f1ff_100%)] p-5 shadow-[0_16px_36px_rgba(37,99,235,0.12)] ring-1 ring-[#dbe7ff]">
              <div className="flex items-center gap-2.5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1d4ed8] text-[11px] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
                  i
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1d4ed8]">Important defaults</p>
              </div>
              <div className="mt-3 space-y-3 text-sm leading-6 text-[#475467]">
                <p>
                  Logging is enabled by default. You can disable logging and adjust retention in Settings.
                </p>
                <p>
                  Self-service password resets for local accounts are also enabled by default. This can be disabled in Settings if you want a tighter access policy.
                </p>
              </div>
            </div>

            {isFreeWelcomePlan ? <div className="owner-welcome-modal-caution mt-4 rounded-[26px] border border-[#fecdca] bg-[linear-gradient(180deg,#fff5f4_0%,#fee4e2_100%)] p-5 shadow-[0_16px_36px_rgba(217,45,32,0.12)] ring-1 ring-[#fecdca]">
              <div className="flex items-center gap-2.5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d92d20] text-[11px] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
                  !
                </div>
                <p className="owner-welcome-modal-caution-label text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b42318]">Caution</p>
              </div>
              <div className="owner-welcome-modal-caution-copy mt-3 space-y-3 text-sm leading-6 text-[#7a271a]">
                <p>
                  Workspaces on the Free plan are automatically <span className="owner-welcome-modal-caution-strong font-semibold text-[#b42318]">deleted</span> after 7 days of inactivity.
                </p>
                <p>
                  Deleted workspaces are <span className="owner-welcome-modal-caution-strong font-semibold text-[#b42318]">unrecoverable</span>.
                </p>
              </div>
            </div> : null}
          </div>

          <div className="owner-welcome-modal-footer flex flex-col gap-3 border-t border-[#eaecf0] bg-[#fcfcfd] px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <p className="max-w-xl text-sm leading-6 text-[#475467]">
              You are starting from a clean, well-structured workspace. Make the essentials solid, then move at your own pace.
            </p>
            <button
              type="button"
              onClick={handleDismissWelcomeModal}
              className="brand-primary-button w-full rounded-full px-6 py-3 text-sm font-semibold sm:w-auto"
            >
              Enter workspace
            </button>
          </div>
        </div>
      </AnimatedModal>
    </AppShell>
    </>
  );
}

export default MainApp;
